import { AuthSystemConfig, LoginCredentials, AuthResponse } from "../types/auth";
import type {
  ExternalSchool,
  ExternalSchoolInput, ExternalSchoolSummary,
  ExternalSchoolOption,
  ExternalResultSummary,
  KarneLookupResponse,
  LgsOpticalImportSummary,
  PartnerOverview,
  PublicSchoolInfo,
  SchoolAdminExam,
} from "../types/external-school";
import { BaseResponse } from "../types/base";
import {
  AppointmentAvailability,
  AppointmentCreatePayload,
  AppointmentItem,
  AppointmentStatus,
  AppointmentListFilters,
  AppointmentProviderFilters,
  AppointmentProviderItem,
  AppointmentSettings,
  AvailableSlotsDateResponse,
  AvailableSlotsWeekResponse,
} from "../types/appointment";
import { SurveyResponse, SurveyFilters } from "../types/survey";
import {
  IlmekContentPack,
  IlmekContentPackFilters,
  IlmekContentPackListResponse,
  IlmekJourney,
  IlmekTurnResult,
} from "../types/ilmek";
import {
  MeetingResponse,
  MeetingDetailResponse,
  MeetingParticipantsResponse,
  CreateMeetingData,
  UpdateMeetingData,
  CreateParticipantData,
  MeetingFilters,
  JoinMeetingResponse,
  MeetAttendanceRecord,
} from "../types/meeting";
import { FileItem, FileListResponse, FileUploadResponse, ProcessFileResponse, UpdateContentResponse, QuestionContent, FileFolder, FolderListResponse } from "../types/file";
import type { PracticeExam, PracticeExamResultSummary, StudentPracticeExamHistory, SeviyeTespitIstek, SeviyeTespitSonuc } from "../types/practice-exam";
import type {
  NavigatorStatusResponse,
  TeachSession,
  TeachStartParams,
  LessonWalkResponse,
  LedgerResponse,
  JournalResponse,
  CoachProfile,
  CoachChatMessage,
  CoachChatResponse,
  CoachTanismaForm,
  CoachTanismaBody,
  CoachTanismaResult,
  YolTeklif,
  ObserveYolView,
  ObserveCallList,
  ObserveYolApplyBody,
  ObservePlanView,
  BolumOneri,
  CoachPlanChatResponse,
  CoachTodayResponse,
  CoachPlanResponse,
  BookCatalogItem,
  BookDaily,
  BookErrorType,
  BookPayload,
  BookReviews,
  BookReadQuota,
  BookResultBody,
  ObserveBooks,
  BookReadResult,
  StudentBook,
  StudentBookSummary,
  PlanItem,
  PlanItemStatus,
  ErrorMark,
  ErrorType,
  MakeupSetResponse,
  CoachReadinessResponse,
  CoachLetter,
  CoachLettersResponse,
  ObserveStudentsResponse,
  ObserveStudentDetail,
  ObserveLetter,
  YksRoadmapResponse,
  YksScenarioResponse,
  YksBeyanDurumu,
} from "../types/navigator";
import type { AIObsOverview, AIObsStudentDetail } from "../types/ai-observation";
import type {
  MonthlySuccessReport,
  MonthlyReportsResponse,
  AvailablePeriod,
  MonthlyReportComparison,
  GetMonthlyReportsOptions,
  DepartmentAvailablePeriod,
  DepartmentMultiMonthReportResponse,
  GetDepartmentMultiMonthReportOptions
} from "../types/monthlyReport";
import type {
  CreateQuestionBankPayload,
  QuestionBankDraft,
  QuestionBankListResponse,
  QuestionBankShareCandidate,
  UpdateQuestionBankPayload,
} from "../types/question-bank";
import type {
  MaarifGeneratePayload,
  MaarifGeneratedPreview,
  MaarifSavePayload,
  MaarifSaveResponse,
} from "../types/maarif-test-generator";
import type {
  MaarifQuestionAuditPayload,
  MaarifQuestionAuditResponse,
  MaarifQuestionContextPayload,
  MaarifQuestionContextResponse,
  MaarifQuestionGenerateFromImagePayload,
  MaarifQuestionGenerateFromImageResponse,
  MaarifQuestionGenerateFromPdfPayload,
  MaarifQuestionPdfJobResponse,
  MaarifQuestionPdfMetadataResponse,
  MaarifQuestionPdfPreviewsResponse,
  MaarifQuestionGeneratePayload,
  MaarifQuestionGenerateResponse,
  MaarifQuestionRecord,
  MaarifQuestionRecordListResponse,
  MaarifQuestionRecordSavePayload,
  MaarifQuestionToQuestionBankPayload,
  MaarifQuestionToQuestionBankResponse,
  MaarifExportTargetListResponse,
  MaarifQuestionRecordUpdatePayload,
  MaarifQuestionRewritePayload,
  MaarifQuestionRewriteResponse,
  MaarifPromptConfig,
} from "../types/maarif-question-wizard";

import { handleUnauthorized } from "../utils/authUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type QuestionImageGenerationContext = {
  testName?: string;
  subject?: string;
  questionNumber?: number | string;
  introText?: string;
  questionText?: string;
  options?: Array<{ key: string; text: string }>;
  correctAnswer?: string | null;
};

// Raporlarım: öğrenci rapor bileşenlerine userId yerine MY_REPORT verilince öğrencinin kendi uçları
// (/report/me/*, deneme PDF'lerinde /practice-exams/:id/my-result/*) çağrılır; ekran aynı kalır.
export const MY_REPORT = "me";
const studentReportBase = (userId: string) => (userId === MY_REPORT ? "/report/me" : `/report/student/${userId}`);

// Koçun telefon araması ayarları (Sistem Ayarları → Telefon Asistanı)
export interface CoachCallSettings {
  enabled: boolean;
  livekitUrl: string;
  livekitApiKey: string;
  livekitApiSecretSet: boolean;
  livekitApiSecretHint: string | null;
  agentName: string;
  publicUrl: string;
  appUrl: string;
  openaiApiKeySet: boolean;
  openaiApiKeyHint: string | null;
  voice: string;
  maxPerRun: number;
  // Asistanın kendini tanıtışı (boşsa "YES Tools platformundan"); bağlantı alanlarıyla birlikte yalnız üst admin değiştirir
  kimlik: string;
  baglantiDuzenlenebilir: boolean;
  // Akademi aramaları (ilk ziyaret, paketli öğrenciyle bağ, Google yorumu, geri arayanı tanıma)
  ilkZiyaret: boolean;
  ilkZiyaretMinDk: number;
  bag: boolean;
  bagAralikGun: number;
  yorumUrl: string;
  yorumKimden: "MEMNUN" | "HERKES";
  akademiKimlik: string;
  gelenAnahtarSet: boolean;
  gelenAnahtarHint: string | null;
  // WhatsApp (YES.Social üzerinden): hoş geldin, yeni ödev, bekleyen iş
  waEnabled: boolean;
  waSendUrl: string;
  waWorkspaceId: string;
  waTokenSet: boolean;
  waTokenHint: string | null;
  waNumara: string;
  waSablonHosGeldin: string;
  waSablonOdev: string;
  waSablonBekleyen: string;
  waHazir: boolean;
  voices: string[];
  etkin: { livekitUrl: string; agentName: string; publicUrl: string; appUrl: string; livekitHazir: boolean; openaiAyarli: boolean };
}
export interface CoachCallSettingsBody {
  enabled: boolean; livekitUrl: string; livekitApiKey: string; livekitApiSecret: string; livekitApiSecretClear: boolean;
  agentName: string; publicUrl: string; appUrl: string; openaiApiKey: string; openaiApiKeyClear: boolean; voice: string; maxPerRun: number; kimlik: string;
  ilkZiyaret: boolean; ilkZiyaretMinDk: number; bag: boolean; bagAralikGun: number; yorumUrl: string; yorumKimden: "MEMNUN" | "HERKES";
  akademiKimlik: string; gelenAnahtar: string; gelenAnahtarClear: boolean;
  waEnabled: boolean; waSendUrl: string; waWorkspaceId: string; waToken: string; waTokenClear: boolean; waNumara: string;
  waSablonHosGeldin: string; waSablonOdev: string; waSablonBekleyen: string;
}

export class ApiService {
  private static getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem("authToken");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  private static async request<T>(endpoint: string, options: RequestInit & { odemeYonlendirmesiz?: boolean } = {}): Promise<T> {
    const method = ((options.method || "GET") as string).toUpperCase();
    const url = `${API_BASE_URL}${endpoint}`;
    const authHeaders = this.getAuthHeaders();

    const mergedHeaders = {
      ...authHeaders,
      ...(options.headers || {}),
    } as Record<string, string>;

    const isFormData = options && options.body && options.body instanceof FormData;
    if (isFormData && mergedHeaders["Content-Type"]) {
      delete mergedHeaders["Content-Type"];
    }

    const defaultOptions: RequestInit = {
      ...options,
      headers: mergedHeaders,
    };

    if (method === "GET") {
      (defaultOptions as any).cache = "no-store";
    }

    try {
      const response = await fetch(url, defaultOptions);

      const contentType = response.headers.get("content-type");
      const hasJsonContent = contentType && contentType.includes("application/json");

      let data = null;

      if (hasJsonContent && response.status !== 204) {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      } else if (response.status === 204) {
        data = null;
      } else {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text };
          }
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          const errorMessage = data?.message || data?.error || "";
          if (errorMessage === "Lütfen önce oturum açın.") {
            handleUnauthorized();
            throw new Error("Yetkisiz Erişim - Giriş Sayfasına Yönlendiriliyor");
          }
        }
        if (response.status === 402) {
          // Sert paywall: deneme doldu / ödeme gerekli → ödeme ekranına yönlendir (emniyet).
          // Ücretsiz sayfanın arka plandaki çağrısı öğrenciyi sayfadan atmasın.
          try {
            if (!options.odemeYonlendirmesiz && typeof window !== "undefined" && !window.location.pathname.startsWith("/payment")) {
              const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
              window.location.assign(`/payment?returnTo=${returnTo}`);
            }
          } catch { /* yok say */ }
          throw new Error(data?.message || "Deneme süreniz doldu. Ödeme gerekiyor.");
        }
        const errorMessage = [data?.message || data?.error || `HTTP Hatası! Durum: ${response.status}`, data?.details]
          .filter(Boolean)
          .join(": ");
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      console.error("API request failed:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("API İsteği Başarısız Oldu");
    }
  }

  private static async requestBlob(endpoint: string, options: RequestInit = {}): Promise<{ blob: Blob; fileName?: string }> {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      let message = `HTTP Hatası! Durum: ${response.status}`;
      try {
        const data = await response.json();
        message = data?.message || data?.error || message;
      } catch (_) { }
      throw new Error(message);
    }

    const contentDisposition = response.headers.get("content-disposition") || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return {
      blob: await response.blob(),
      fileName: match?.[1],
    };
  }

  static async getAuthSystemConfig(): Promise<AuthSystemConfig> {
    return this.getApi("system/auth");
  }

  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.postApi("auth", credentials);
  }

  static async logout(): Promise<void> {
    return this.postApi("auth/logout", {});
  }

  static async getMe(): Promise<any> {
    const data = await this.getApi("auth/me");
    try {
      const prefs = (data && (data as any).preferences) || {};
      const normalized = {
        themeType: typeof prefs.themeType === "number" ? prefs.themeType : 0,
        bgPatternType: typeof prefs.bgPatternType === "number" ? prefs.bgPatternType : 0,
        bgAnimated: typeof prefs.bgAnimated === "boolean" ? prefs.bgAnimated : true,
        showSeconds: typeof prefs.showSeconds === "boolean" ? prefs.showSeconds : false,
      };
      const existing = localStorage.getItem("userPreferences");
      const next = JSON.stringify(normalized);
      if (existing !== next) {
        localStorage.setItem("userPreferences", next);
      }
    } catch (_) { }
    return data;
  }

  static async getPaymentAccess(): Promise<any> {
    return this.getApi("payments/access");
  }

  static async getPaymentCatalog(): Promise<any> {
    return this.getApi("payments/catalog");
  }

  static async getPaymentQuote(payload: {
    packageId: string;
    provider: string;
    installment: number;
    cardBin?: string;
    referralCode?: string;
  }): Promise<any> {
    return this.postApi("payments/quote", payload);
  }

  static async startPaymentCheckout(payload: {
    packageId: string;
    provider: string;
    quoteToken: string;
    billing?: any;
    // Ön bilgilendirme + sözleşme onayı (sunucu kanıt olarak siparişe yazar)
    yasal?: { onBilgilendirme: boolean; sozlesmeler: boolean };
  }): Promise<any> {
    return this.postApi("payments/checkout", payload);
  }

  // Mesafeli satış ön bilgilendirme formu (siparişe özel)
  static async getPaymentOnBilgilendirme(payload: { packageId: string; referralCode?: string; billing?: any }): Promise<{
    surum: string;
    baslik: string;
    bolumler: { baslik: string; metin: string }[];
  }> {
    return this.postApi("payments/on-bilgilendirme", payload);
  }

  // Ödeme sayfasındaki davet kodu: bu pakette ne kadar indirim getirir (30 gün köprüsüyle birlikte).
  static async checkPaymentReferral(payload: { code?: string; packageId: string }): Promise<any> {
    return this.postApi("payments/referral/check", payload);
  }

  // Yönetici: davetle yapılan satışlar ve getirene verilecek ödüller.
  static async getPaymentReferrals(filters?: { state?: string; search?: string }): Promise<any[]> {
    const query = new URLSearchParams();
    if (filters?.state) query.set("state", filters.state);
    if (filters?.search) query.set("search", filters.search);
    return this.request<any[]>(`/payments/admin/referrals${query.toString() ? `?${query}` : ""}`);
  }

  static async rewardPaymentReferral(referralId: string, note?: string): Promise<any> {
    return this.postApi(`payments/admin/referrals/${referralId}/reward`, { note });
  }

  // Kişisel davet linklerini Excel olarak indirir (kodu olmayana kod üretilir).
  static async exportPaymentInviteLinks(params: {
    origin: string;
    departmentIds?: string[];
    registeredFrom?: string;
    registeredTo?: string;
    message?: string;
  }): Promise<Blob> {
    const query = new URLSearchParams();
    query.set("origin", params.origin);
    if (params.departmentIds?.length) query.set("departmentIds", params.departmentIds.join(","));
    if (params.registeredFrom) query.set("registeredFrom", params.registeredFrom);
    if (params.registeredTo) query.set("registeredTo", params.registeredTo);
    if (params.message) query.set("message", params.message);
    const response = await fetch(`${API_BASE_URL}/payments/admin/referrals/invite-export?${query}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP Hatası! Durum: ${response.status}`);
    return response.blob();
  }

  static async getPaymentBillingProfile(): Promise<any> {
    return this.getApi("payments/billing-profile");
  }

  static async getPaymentOrder(orderId: string): Promise<any> {
    return this.getApi(`payments/orders/${orderId}`);
  }

  static async getPaymentAdminSettings(): Promise<any> {
    return this.getApi("payments/admin/settings");
  }

  static async updatePaymentAdminSettings(payload: any): Promise<any> {
    return this.putApi("payments/admin/settings", payload);
  }

  // iyzico panelindeki gerçek taksit politikası, KAYITLI paketlerin beyanıyla karşılaştırılır.
  static async getPaymentInstallmentPolicy(): Promise<any> {
    return this.getApi("payments/admin/installment-policy");
  }

  static async getPaymentOrders(filters?: {
    status?: string;
    provider?: string;
    userId?: string;
    source?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<any[]> {
    const query = new URLSearchParams();
    if (filters?.status) query.set("status", filters.status);
    if (filters?.provider) query.set("provider", filters.provider);
    if (filters?.userId) query.set("userId", filters.userId);
    if (filters?.source) query.set("source", filters.source);
    if (filters?.search) query.set("search", filters.search);
    if (filters?.startDate) query.set("startDate", filters.startDate);
    if (filters?.endDate) query.set("endDate", filters.endDate);
    if (filters?.limit) query.set("limit", String(filters.limit));
    return this.request<any[]>(`/payments/admin/orders${query.toString() ? `?${query}` : ""}`);
  }

  // Bekleyen/ödendi siparişi sağlayıcıdan sorgular; kaybolan callback'i telafi eder.
  static async reconcilePaymentOrder(orderId: string): Promise<any> {
    return this.postApi(`payments/admin/orders/${orderId}/reconcile`, {});
  }

  // Ekrandaki filtrelerle Paraşüt "Müşteri ve Tedarikçiler" içe aktarma dosyasını indirir.
  static async exportPaymentOrders(filters?: {
    status?: string;
    provider?: string;
    userId?: string;
    source?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> {
    const query = new URLSearchParams();
    if (filters?.status) query.set("status", filters.status);
    if (filters?.provider) query.set("provider", filters.provider);
    if (filters?.userId) query.set("userId", filters.userId);
    if (filters?.source) query.set("source", filters.source);
    if (filters?.search) query.set("search", filters.search);
    if (filters?.startDate) query.set("startDate", filters.startDate);
    if (filters?.endDate) query.set("endDate", filters.endDate);

    const response = await fetch(`${API_BASE_URL}/payments/admin/orders/export${query.toString() ? `?${query}` : ""}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP Hatası! Durum: ${response.status}`);
    return response.blob();
  }

  static async getPaymentSubscriptions(filters?: { search?: string; includeExpired?: boolean; limit?: number }): Promise<any[]> {
    const query = new URLSearchParams();
    if (filters?.search) query.set("search", filters.search);
    if (filters?.includeExpired) query.set("includeExpired", "true");
    if (filters?.limit) query.set("limit", String(filters.limit));
    return this.request<any[]>(`/payments/admin/subscriptions${query.toString() ? `?${query}` : ""}`);
  }

  static async getGrantablePaymentPackages(): Promise<any> {
    return this.getApi("payments/admin/packages");
  }

  static async grantPaymentPackage(payload: {
    userId: string;
    packageId: string;
    customEndsAt?: string;
    method: "cash" | "free";
    amount?: number;
    note?: string;
    billing?: any;
  }): Promise<any> {
    return this.postApi("payments/admin/grants", payload);
  }

  static async cancelPaymentGrant(orderId: string): Promise<any> {
    return this.postApi(`payments/admin/grants/${orderId}/cancel`, {});
  }

  // assignmentId verilirse yalnız o paket, verilmezse kişinin tüm geçerli paketleri kapanır.
  static async endPaymentSubscription(userId: string, assignmentId?: string): Promise<any> {
    return this.postApi(`payments/admin/subscriptions/${userId}/end`, assignmentId ? { assignmentId } : {});
  }

  static async getTemporaryAssignments(userId?: string, activeOnly = false): Promise<any[]> {
    const query = new URLSearchParams();
    if (userId) query.set("userId", userId);
    if (activeOnly) query.set("activeOnly", "true");
    return this.request<any[]>(`/payments/assignments${query.toString() ? `?${query}` : ""}`);
  }

  static async createTemporaryAssignment(payload: {
    userId: string;
    targetDepartmentId: string;
    startsAt: string;
    endsAt: string;
    reason?: string;
  }): Promise<any> {
    return this.postApi("payments/assignments", payload);
  }

  static async extendTemporaryAssignment(assignmentId: string, endsAt: string): Promise<any> {
    return this.patchApi(`payments/assignments/${assignmentId}/extend`, { endsAt });
  }

  static async endTemporaryAssignment(assignmentId: string): Promise<any> {
    return this.postApi(`payments/assignments/${assignmentId}/end`, {});
  }

  static async changePassword(payload: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<any> {
    return this.postApi("auth/change-password", payload);
  }

  public static async updateMyPreferences(payload: {
    themeType?: number;
    bgAnimated?: boolean;
    showSeconds?: boolean;
    bgPatternType?: number;
  }): Promise<{
    preferences: {
      themeType?: number;
      bgAnimated?: boolean;
      showSeconds?: boolean;
      bgPatternType?: number;
    };
  }> {
    return this.request<{
      preferences: {
        themeType?: number;
        bgAnimated?: boolean;
        showSeconds?: boolean;
        bgPatternType?: number;
      };
    }>("/users/me/preferences", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  public static async updateMySidebarSort(sidebarSort: string[]): Promise<{ sidebarSort: string[] }> {
    return this.request<{ sidebarSort: string[] }>("/users/me/sidebar-sort", {
      method: "PUT",
      body: JSON.stringify(sidebarSort),
    });
  }

  static async getApi(path: string, page?: number, search?: string): Promise<BaseResponse | any> {
    const params = new URLSearchParams();
    if (page) params.append("page", page.toString());
    if (search && search.trim()) params.append("search", search.trim());
    const queryString = params.toString();
    const endpoint = queryString ? `/${path}${path.includes("?") ? "&" : "?"}${queryString}` : `/${path}`;
    return this.request<BaseResponse | any>(endpoint);
  }

  static async postApi(path: string, data: any): Promise<any> {
    return this.request<any>(`/${path}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  static async putApi(path: string, data: any): Promise<any> {
    return this.request<any>(`/${path}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  static async patchApi(path: string, data: any): Promise<any> {
    return this.request<any>(`/${path}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  static async deleteApi(path: string): Promise<any> {
    return this.request<any>(`/${path}`, {
      method: "DELETE",
    });
  }

  public static async getAppointmentProviders(
    filters?: AppointmentProviderFilters
  ): Promise<BaseResponse & { data: AppointmentProviderItem[] }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    if (filters?.sortBy) params.append("sortBy", String(filters.sortBy));
    if (filters?.sortOrder) params.append("sortOrder", String(filters.sortOrder));
    if (filters?.search) params.append("search", String(filters.search));
    const qs = params.toString();
    return this.request<BaseResponse & { data: AppointmentProviderItem[] }>(`/appointments/providers${qs ? `?${qs}` : ""}`);
  }

  public static async getAvailableSlots(params: {
    providerId: string;
    date?: string;
    week?: string;
  }): Promise<AvailableSlotsDateResponse | AvailableSlotsWeekResponse | any> {
    const qs = new URLSearchParams();
    qs.append("providerId", params.providerId);
    if (params.date) qs.append("date", params.date);
    if (params.week) qs.append("week", params.week);
    const query = qs.toString();
    return this.request(`/appointments/available-slots?${query}`);
  }

  public static async createAppointment(payload: AppointmentCreatePayload): Promise<AppointmentItem[]> {
    return this.postApi("appointments", payload);
  }

  public static async getMyBookings(filters?: AppointmentListFilters): Promise<BaseResponse & { data: AppointmentItem[] }> {
    const qs = new URLSearchParams();
    if (filters?.page) qs.append("page", String(filters.page));
    if (filters?.limit) qs.append("limit", String(filters.limit));
    if (filters?.sortBy) qs.append("sortBy", String(filters.sortBy));
    if (filters?.sortOrder) qs.append("sortOrder", String(filters.sortOrder));
    if (filters?.search) qs.append("search", String(filters.search));
    if (filters?.startDate) qs.append("startDate", String(filters.startDate));
    if (filters?.endDate) qs.append("endDate", String(filters.endDate));
    if (filters?.status) qs.append("status", String(filters.status));
    const query = qs.toString();
    return this.request<BaseResponse & { data: AppointmentItem[] }>(`/appointments/my-bookings${query ? `?${query}` : ""}`);
  }

  public static async getMyProviderAppointments(filters?: AppointmentListFilters): Promise<BaseResponse & { data: AppointmentItem[] }> {
    const qs = new URLSearchParams();
    if (filters?.page) qs.append("page", String(filters.page));
    if (filters?.limit) qs.append("limit", String(filters.limit));
    if (filters?.sortBy) qs.append("sortBy", String(filters.sortBy));
    if (filters?.sortOrder) qs.append("sortOrder", String(filters.sortOrder));
    if (filters?.search) qs.append("search", String(filters.search));
    if (filters?.startDate) qs.append("startDate", String(filters.startDate));
    if (filters?.endDate) qs.append("endDate", String(filters.endDate));
    if (filters?.status) qs.append("status", String(filters.status));
    const query = qs.toString();
    return this.request<BaseResponse & { data: AppointmentItem[] }>(`/appointments/my-appointments${query ? `?${query}` : ""}`);
  }

  public static async approveAppointment(appointmentId: string, body?: { description?: string }): Promise<AppointmentItem> {
    return this.postApi(`appointments/${appointmentId}/approve`, body || {});
  }

  public static async rejectAppointment(appointmentId: string, body?: { description?: string; reason?: string }): Promise<AppointmentItem> {
    return this.postApi(`appointments/${appointmentId}/reject`, body || {});
  }

  public static async updateAppointment(
    appointmentId: string,
    body: { description?: string; status?: AppointmentStatus }
  ): Promise<AppointmentItem> {
    return this.putApi(`appointments/${appointmentId}`, body);
  }

  public static async markAttendance(
    appointmentId: string,
    attendanceStatus: "UNMARKED" | "ATTENDED" | "NO_SHOW"
  ): Promise<AppointmentItem> {
    return this.postApi(`appointments/${appointmentId}/attendance`, { attendanceStatus });
  }

  public static async getAppointmentSettings(): Promise<AppointmentSettings> {
    return this.getApi("appointments/settings");
  }

  public static async updateAppointmentSettings(data: Partial<AppointmentSettings>): Promise<AppointmentSettings> {
    return this.putApi("appointments/settings", data);
  }

  public static async getAppointmentAvailability(providerId?: string): Promise<AppointmentAvailability> {
    if (providerId) {
      return this.getApi(`appointments/availability/${providerId}`);
    }
    return this.getApi("appointments/availability");
  }

  public static async updateAppointmentAvailability(data: AppointmentAvailability): Promise<AppointmentAvailability> {
    const { weekly, defaultCapacity, workingHours, slotDuration, metadata } = data || ({} as AppointmentAvailability);
    return this.request<AppointmentAvailability>(`/appointments/availability`, {
      method: "PUT",
      body: JSON.stringify({
        weekly,
        defaultCapacity,
        workingHours,
        slotDuration,
        metadata,
      }),
    });
  }

  public static async getAppointmentManageProviders(filters?: AppointmentProviderFilters): Promise<BaseResponse & { data: AppointmentProviderItem[] }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    if (filters?.sortBy) params.append("sortBy", String(filters.sortBy));
    if (filters?.sortOrder) params.append("sortOrder", String(filters.sortOrder));
    if (filters?.search) params.append("search", String(filters.search));
    const qs = params.toString();
    return this.request<BaseResponse & { data: AppointmentProviderItem[] }>(`/appointments/manage-providers${qs ? `?${qs}` : ""}`);
  }

  public static async updateAppointmentAvailabilityForProvider(providerId: string, data: AppointmentAvailability): Promise<AppointmentAvailability> {
    const { weekly, defaultCapacity, workingHours, slotDuration, metadata } = data || ({} as AppointmentAvailability);
    return this.request<AppointmentAvailability>(`/appointments/availability/${providerId}`, {
      method: "PUT",
      body: JSON.stringify({
        weekly,
        defaultCapacity,
        workingHours,
        slotDuration,
        metadata,
      }),
    });
  }

  public static async deleteAppointmentAvailability(): Promise<{
    message: string;
    providerId: string;
  }> {
    return this.deleteApi("appointments/availability");
  }

  public static async getAppointmentReportWeeks(): Promise<any> {
    return this.getApi("appointments/reports/weekly");
  }

  public static async getWeeklyAppointmentReport(data: { weekNumbers: number[]; page?: number; limit?: number }): Promise<any> {
    return this.postApi("appointments/reports/weekly-appointments", data);
  }

  public static async exportWeeklyAppointmentReport(weekNumbers: number[]): Promise<Blob> {
    const url = `${API_BASE_URL}/appointments/reports/weekly-appointments/export`;
    const response = await fetch(url, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ weekNumbers }),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  public static async exportAttendanceReport(filters?: {
    startDate?: string;
    endDate?: string;
    providerId?: string;
    userId?: string;
    departmentId?: string;
    status?: string;
    attendanceStatus?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (filters?.startDate) queryParams.append("startDate", filters.startDate);
    if (filters?.endDate) queryParams.append("endDate", filters.endDate);
    if (filters?.providerId) queryParams.append("providerId", filters.providerId);
    if (filters?.userId) queryParams.append("userId", filters.userId);
    if (filters?.departmentId) queryParams.append("departmentId", filters.departmentId);
    if (filters?.status) queryParams.append("status", filters.status);
    if (filters?.attendanceStatus) queryParams.append("attendanceStatus", filters.attendanceStatus);

    const url = `${API_BASE_URL}/appointments/reports/attendance/export?${queryParams.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  public static async createAppointmentForUser(payload: {
    userId: string;
    date: string;
    times: Array<{ started: string; ended: string }>;
    description?: string;
    metadata?: any;
  }): Promise<AppointmentItem[]> {
    return this.postApi("appointments/create-for-user", payload);
  }

  public static async createAppointmentForUserByAdmin(providerId: string, payload: {
    userId: string;
    date: string;
    times: Array<{ started: string; ended: string }>;
    description?: string;
    metadata?: any;
  }): Promise<AppointmentItem[]> {
    return this.postApi(`appointments/create-for-user/${providerId}`, payload);
  }

  public static async getAppointments(filters?: AppointmentListFilters & { providerId?: string }): Promise<BaseResponse & { data: AppointmentItem[] }> {
    const qs = new URLSearchParams();
    if (filters?.page) qs.append("page", String(filters.page));
    if (filters?.limit) qs.append("limit", String(filters.limit));
    if (filters?.sortBy) qs.append("sortBy", String(filters.sortBy));
    if (filters?.sortOrder) qs.append("sortOrder", String(filters.sortOrder));
    if (filters?.search) qs.append("search", String(filters.search));
    if (filters?.startDate) qs.append("startDate", String(filters.startDate));
    if (filters?.endDate) qs.append("endDate", String(filters.endDate));
    if (filters?.status) qs.append("status", String(filters.status));
    if (filters?.providerId) qs.append("providerId", String(filters.providerId));
    const query = qs.toString();
    return this.request<BaseResponse & { data: AppointmentItem[] }>(`/appointments${query ? `?${query}` : ""}`);
  }

  static async createUser(data: any, file?: File): Promise<any> {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "metadata" && typeof value === "object") {
          Object.entries(value).forEach(([metaKey, metaValue]) => {
            if (metaValue !== undefined && metaValue !== null && metaValue !== "") {
              formData.append(`metadata[${metaKey}]`, String(metaValue));
            }
          });
          return;
        }
        formData.append(key, String(value));
      });
      return this.request<any>(`/users`, {
        method: "POST",
        body: formData,
      });
    }
    return this.postApi("users", data);
  }

  static async updateUser(userId: string, data: any, file?: File) {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "metadata" && typeof value === "object") {
          Object.entries(value).forEach(([metaKey, metaValue]) => {
            if (metaValue !== undefined && metaValue !== null && metaValue !== "") {
              formData.append(`metadata[${metaKey}]`, String(metaValue));
            }
          });
          return;
        }
        formData.append(key, String(value));
      });
      return this.request<any>(`/users/${userId}`, {
        method: "PUT",
        body: formData,
      });
    }
    return this.putApi(`users/${userId}`, data);
  }

  static async deleteUser(userId: string) {
    return this.deleteApi(`users/${userId}`);
  }

  static async getUser(userId: string): Promise<any> {
    return this.getApi(`users/${userId}`);
  }

  static async exportUsers(departmentId?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (departmentId) {
      params.append("departmentId", departmentId);
    }
    const queryString = params.toString();
    const url = `/users/export${queryString ? `?${queryString}` : ""}`;
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  static async startUsersImportFromExcel(departmentId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append("departmentId", departmentId);
    formData.append("file", file);

    return this.request<any>("/users/import/excel", {
      method: "POST",
      body: formData,
    });
  }

  static async getUsersImportStatus(jobId: string): Promise<any> {
    return this.request<any>(`/users/import/excel/${encodeURIComponent(jobId)}`);
  }

  static async createDepartment(data: any): Promise<any> {
    return this.postApi("departments", data);
  }

  static async updateDepartment(departmentId: string, data: any) {
    return this.putApi(`departments/${departmentId}`, data);
  }

  static async deleteDepartment(departmentId: string) {
    const res = await this.deleteApi(`departments/${departmentId}`);
    try {
      const key = "deletedDepartmentIds";
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      const arr = Array.isArray(list) ? list : [];
      if (!arr.includes(departmentId)) {
        arr.push(departmentId);
        localStorage.setItem(key, JSON.stringify(arr));
      }
    } catch (_) { }
    return res;
  }

  static async getDepartmentPermissions(departmentId: string): Promise<any[]> {
    return await this.getApi(`departments/${departmentId}/permissions`);
  }

  static async updateDepartmentPermissions(
    departmentId: string,
    permissions: string[],
    options?: { childDepartment?: boolean }
  ): Promise<any> {
    const body: any = { permissions };
    if (options?.childDepartment) {
      body.childDepartment = true;
    }

    return await this.request<any>(`/departments/${departmentId}/permissions`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
  }

  static async getCalendars(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.search) params.append("search", filters.search);
    if (filters?.referenceType) params.append("referenceType", String(filters.referenceType));
    if (filters?.year) params.append("year", String(filters.year));
    if (filters?.month) params.append("month", String(filters.month));

    const queryString = params.toString();
    const endpoint = queryString ? `/calendars?${queryString}` : "/calendars";

    return this.request<any>(endpoint);
  }

  static async getAllCalendars(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.year) params.append("year", String(filters.year));
    if (filters?.month) params.append("month", String(filters.month));
    if (filters?.types) params.append("types", String(filters.types));

    const queryString = params.toString();
    const endpoint = queryString ? `/calendars/all?${queryString}` : "/calendars/all";

    return this.request<any>(endpoint);
  }

  static async createCalendar(data: any): Promise<any> {
    return this.postApi("calendars", data);
  }

  static async updateCalendar(calendarId: string, data: any) {
    return this.putApi(`calendars/${calendarId}`, data);
  }

  static async deleteCalendar(calendarId: string) {
    return this.deleteApi(`calendars/${calendarId}`);
  }

  static async getLessons(page?: number, limit?: number, search?: string, sortBy?: string, sortOrder?: string): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    if (search) params.append("search", search);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);

    const queryString = params.toString();
    return this.getApi(`lessons${queryString ? `?${queryString}` : ""}`);
  }

  static async getLesson(lessonId: string): Promise<any> {
    return this.getApi(`lessons/${lessonId}`);
  }

  static async getLessonPreview(lessonId: string): Promise<any> {
    return this.getApi(`lessons/preview/${lessonId}`);
  }

  static async getRecentLessonContentTracking(limit = 3): Promise<{
    data: Array<{
      id: string;
      status: string;
      duration?: number | null;
      startDate?: string | null;
      endDate?: string | null;
      updatedAt: string;
      content: {
        id: string;
        title: string;
        type: string;
        metadata?: any;
      };
      section: {
        id: string;
        title: string;
      } | null;
      lesson: {
        id: string;
        title: string;
      } | null;
    }>;
  }> {
    return this.getApi(`lessons/contents/recent-tracking?limit=${Math.min(Math.max(limit, 1), 12)}`);
  }

  public static async getLessonsDepartments(page = 1, limit = 20, search?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(Math.min(limit, 20)));
    if (search && search.trim()) params.append("search", search.trim());
    const qs = params.toString();
    return this.request<any>(`/lessons/departments${qs ? `?${qs}` : ""}`);
  }

  static async createLesson(data: any, file?: File): Promise<any> {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(data || {}).forEach(([key, value]) => {
        if (key === "metadata") {
          return;
        }
        if (value !== undefined && value !== null) {
          const stringified = typeof value === "object" ? JSON.stringify(value) : String(value);
          formData.append(key, stringified);
        }
      });
      return this.request<any>("/lessons", {
        method: "POST",
        body: formData,
      });
    }
    return this.request<any>("/lessons", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  static async updateLesson(lessonId: string, data: any): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}`, {
      method: "PUT",
      body: data,
    });
  }

  static async deleteLesson(lessonId: string): Promise<any> {
    return this.deleteApi(`lessons/${lessonId}`);
  }

  static async getLessonSections(lessonId: string): Promise<any> {
    return this.getApi(`lessons/${lessonId}/sections`);
  }

  static async getSection(lessonId: string, sectionId: string): Promise<any> {
    return this.getApi(`lessons/${lessonId}/sections/${sectionId}`);
  }

  public static async getLessonSectionTests(lessonId: string, sectionId: string): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/tests`);
  }

  public static async getTestReports(payload: {
    lessonId: string;
    departmentIds: string[];
    topicId: string;
    testIds: string[];
    status?: number;
  }): Promise<any> {
    return this.postApi("report/test", payload);
  }

  public static async resetUserTest(testId: string, userId: string): Promise<any> {
    return this.deleteApi(`report/test/${testId}/user/${userId}/reset`);
  }

  static async createSection(lessonId: string, data: any): Promise<any> {
    return this.postApi(`lessons/${lessonId}/sections`, data);
  }

  static async updateSection(lessonId: string, sectionId: string, data: any): Promise<any> {
    return this.putApi(`lessons/${lessonId}/sections/${sectionId}`, data);
  }

  static async deleteSection(lessonId: string, sectionId: string): Promise<any> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}`);
  }

  static async getSectionContents(lessonId: string, sectionId: string): Promise<any> {
    return this.getApi(`lessons/${lessonId}/sections/${sectionId}/contents`);
  }

  static async getContent(lessonId: string, sectionId: string, contentId: string): Promise<any> {
    return this.getApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}`);
  }

  static async completeExam(contentId: string, answers: any): Promise<any> {
    return this.postApi(`lessons/contents/${contentId}/questions/complete`, answers);
  }

  static async saveExam(contentId: string, answers: any): Promise<any> {
    return this.postApi(`lessons/contents/${contentId}/questions/save`, answers);
  }

  static async sendUserTracking(contentId: string, body: any): Promise<any> {
    return this.postApi(`lessons/contents/${contentId}/tracking`, body);
  }

  // Çalışma kâğıdı (WORKSHEET): sayfalar (cevap anahtarı yok) + her sayfanın son gönderimi
  static async getWorksheet(contentId: string): Promise<any> {
    return this.request<any>(`/lessons/worksheets/${encodeURIComponent(contentId)}`);
  }

  // source: "yazim" (sayfaya yazılan şeffaf çizim katmanı) | "foto" (kâğıt fotoğrafı) | "gorsel" (kendi görseli)
  static async submitWorksheetPage(
    contentId: string,
    pageNo: number,
    payload: { source: "yazim" | "foto" | "gorsel"; images: Blob[]; answerText?: string }
  ): Promise<any> {
    const formData = new FormData();
    formData.append("source", payload.source);
    if (payload.answerText) formData.append("answerText", payload.answerText);
    payload.images.forEach((image, index) => {
      const name = image instanceof File ? image.name : `calisma-${index + 1}.${image.type === "image/png" ? "png" : "jpg"}`;
      formData.append("images", image, name);
    });
    return this.request<any>(`/lessons/worksheets/${encodeURIComponent(contentId)}/pages/${pageNo}/submissions`, {
      method: "POST",
      body: formData,
    });
  }

  static async createContent(lessonId: string, sectionId: string, data: any): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents`, {
      method: "POST",
      body: data,
    });
  }

  static async updateContent(lessonId: string, sectionId: string, contentId: string, data: any, file?: File): Promise<any> {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const stringified = typeof value === "object" ? JSON.stringify(value) : String(value);
          formData.append(key, stringified);
        }
      });
      return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}`, {
        method: "PUT",
        body: formData,
      });
    }
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  static async deleteContent(lessonId: string, sectionId: string, contentId: string): Promise<any> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}`);
  }

  static async getLessonResponsible(lessonId: string, userId?: string): Promise<any> {
    let url = `lessons/${lessonId}/responsible`;
    if (userId) {
      url += `?userId=${userId}`;
    }
    return this.getApi(url);
  }

  static async createLessonResponsible(lessonId: string, data: any): Promise<any> {
    return this.postApi(`lessons/${lessonId}/responsible`, data);
  }

  static async deleteLessonResponsible(lessonId: string, data: any): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/responsible`, {
      method: "DELETE",
      body: JSON.stringify(data),
    });
  }

  static async sendChat(data: any): Promise<any> {
    return this.postApi("ai/chat", data);
  }

  static async getChatHistory(conversationId?: string): Promise<any> {
    return this.getApi(`ai/chat${conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : ""}`);
  }

  static async createDeepConversation(): Promise<any> {
    return this.postApi("ai/chat/new", {});
  }

  static async sendLessonQuestionAssist(data: {
    lessonContentId: string;
    questionId: string;
    message: string;
    history?: Array<{ type: "user" | "assistant"; content: string }>;
  }): Promise<{ output: string; model: string; examStatus: string }> {
    return this.postApi("ai/lesson-question-assist", data);
  }

  static async generateQuestionImage(payload: {
    prompt: string;
    aspectRatio?: string;
    questionContext?: QuestionImageGenerationContext;
  }): Promise<{ mimeType: string; base64: string; model: string }> {
    return this.postApi("ai/generate-image", payload);
  }

  static async generateQuestionImageFromSketch(payload: {
    prompt: string;
    sketchBase64: string;
    sketchMimeType?: string;
    aspectRatio?: string;
    questionContext?: QuestionImageGenerationContext;
  }): Promise<{ mimeType: string; base64: string; model: string }> {
    return this.postApi("ai/generate-image-from-sketch", payload);
  }

  static async generateQuestionImageFromReference(payload: {
    prompt: string;
    referenceBase64: string;
    referenceMimeType?: string;
    aspectRatio?: string;
    questionContext?: QuestionImageGenerationContext;
  }): Promise<{ mimeType: string; base64: string; model: string }> {
    return this.postApi("ai/generate-image-from-reference", payload);
  }

  static async correctQuestionText(payload: {
    text: string;
  }): Promise<{ text: string; model: string }> {
    return this.postApi("ai/correct-text", payload);
  }

  static async transcribeAudio(audioBlob: Blob, language: string = 'tr'): Promise<{ text: string }> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    formData.append('language', language);

    const token = localStorage.getItem("authToken");
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/ai/transcribe`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP Hatası! Durum: ${response.status}`);
    }

    return response.json();
  }

  // ESKİ EKSİK GİDERME SARMALAYICILARI KALDIRILDI (9 Ağu 2026): startTutorSession,
  // checkTutorAnswers, getTutorGainDetails, getTopicSessionDetails, startTopicTutorSession,
  // checkTopicTutorAnswers. Uçları da backend'den kaldırıldı — ayrı motor, ayrı kapama
  // eşiği, telemetri yok. Öğretim tek yerden: startTeach + sendAITeacherMessage.

  static async getGroupedDeficiencies(filters?: {
    isResolved?: boolean;
    lessonId?: string;
    topicId?: string;
    page?: number;
    limit?: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.isResolved !== undefined) params.append("isResolved", String(filters.isResolved));
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    const qs = params.toString();
    return this.getApi(`ai/tutor/grouped-deficiencies${qs ? `?${qs}` : ""}`);
  }

  static async sendAITeacherMessage(payload: {
    message?: string;
    context: Record<string, any>;
    history?: Array<{ role: "user" | "assistant"; content: string }>;
  }): Promise<{
    message: string;
    toolCalls?: Array<{ id?: string; name: "show_pdf_on_canvas" | "open_lesson_video" | "open_simulation" | "open_practice_test"; arguments: Record<string, any> }>;
    context?: Record<string, any>;
    model?: string;
    // Geçiş köprüsü sistem notudur (AI balonu değil, geçmişe girmez)
    gainTransitionNotice?: boolean;
    // Yeni kazanımı model tanıtmadı — panel geçişten sonra tanıttırır
    gainIntroPending?: boolean;
  }> {
    return this.postApi("ai/tutor/ai-teacher/chat", payload);
  }

  static async createAITeacherLiveSession(payload: {
    message?: string;
    context: Record<string, any>;
    history?: Array<{ role: "user" | "assistant"; content: string }>;
  }): Promise<{
    mode: "live";
    model: string;
    token: string;
    websocketUrl: string;
    expiresAt?: string;
    context?: Record<string, any>;
    setup: Record<string, any>;
    clientContent: {
      turns: Array<{ role: "user" | "model"; parts: Array<{ text: string }> }>;
      turnComplete: boolean;
    };
  }> {
    return this.postApi("ai/tutor/ai-teacher/live-session", payload);
  }

  static async getAITeacherPracticeTest(payload: {
    gainId?: string;
    context: Record<string, any>;
  }): Promise<{
    questions: any[];
    source?: string;
    message?: string;
  }> {
    return this.postApi("ai/tutor/ai-teacher/practice-test", payload);
  }

  static async completeAITeacherPracticeTest(payload: {
    gainId: string;
    answers: Array<{ questionId: string | number; selected: string | null }>;
    context?: Record<string, any>;
  }): Promise<{ passed: boolean; correct: number; total: number; percent: number; resolvedCount: number }> {
    return this.postApi("ai/tutor/ai-teacher/practice-test/complete", payload);
  }

  // Testle ölçülemeyen kazanımı (tanım-düzeyi ya da 3'ten az taze soru) anlatım sonunda kapatır.
  // Sunucu havuzu yeniden sayar: ölçülebilir kazanım buradan kapanmaz (closed:false, reason:'testable').
  static async completeAITeacherGainTeaching(payload: {
    gainId: string;
    context?: Record<string, any>;
    // öğrenci bu kazanımda gerçekten çalıştı mı — sunucu taught olmadan kapatmaz
    taught?: boolean;
  }): Promise<{ closed: boolean; reason: string; freshQuestions?: number; resolvedCount: number }> {
    return this.postApi("ai/tutor/ai-teacher/gain/complete-teaching", payload);
  }

  // Kazanım geçişinin TEK kapısı (test geçilince, "Sıradaki kazanım" düğmesi, AI'nın geçiş aracı).
  static async advanceAITeacherGain(payload: {
    fromIndex: number;
    reason: "test-passed" | "button" | "model-tool" | "close";
    taught: boolean;
    /** Son kazanımda konuyu bitir: kapanış denenir, kapanırsa oturum tamamlanır */
    finish?: boolean;
    /** "Yine de bitir": eksik açık kalsa da oturum tamamlanır (yeniden açılınca son kazanıma dönülmez) */
    force?: boolean;
    context?: Record<string, any>;
  }): Promise<{ advanced: boolean; currentGainIndex: number; decision: string; resolvedCount: number; leftOpen: boolean; closeReason: string | null; sessionCompleted?: boolean }> {
    return this.postApi("ai/tutor/ai-teacher/gain/advance", payload);
  }

  // İstemcide sessizce olan aksaklıkların kaydı (sağlık raporu için) — öğrenciyi etkilemez.
  static async reportAITeacherEvent(payload: {
    type: string;
    detail?: Record<string, any>;
    context?: Record<string, any>;
  }): Promise<{ recorded: boolean }> {
    return this.postApi("ai/tutor/ai-teacher/event", payload);
  }

  static async prepareSessionPlan(gainIds: string[]): Promise<{ sessionPlanId: string; status: string }> {
    return this.postApi("ai/session-plan/prepare", { gainIds });
  }

  static async prepareSessionPlanFromSection(sectionId: string, options?: { refresh?: boolean }): Promise<{
    sessionPlanId: string | null;
    status: "ready" | "no_data" | "not_found";
    clusterCount?: number;
    message?: string;
  }> {
    return this.postApi("ai/session-plan/prepare-from-section", {
      sectionId,
      ...(options?.refresh ? { refresh: true } : {}),
    });
  }

  static async getSectionLearningPlan(sectionId: string, options?: { refresh?: boolean }): Promise<{
    section: { id: string; title: string; lessonId: string };
    testCount: number;
    wrongCount: number;
    conceptCount: number;
    conceptGroups: Array<{
      conceptKey: string;
      conceptLabel: string;
      wrongCount: number;
      distinctExamCount: number;
      distinctQuestionCount: number;
      score: number;
      videoSegments: Array<{ videoId: string; startTime: string; endTime: string }>;
    }>;
    materials: {
      videos: Array<{ id: string; title: string }>;
      pdfs: Array<{ id: string; title: string }>;
    };
    message?: string;
  }> {
    const qs = options?.refresh ? "?refresh=1" : "";
    return this.getApi(`lessons/sections/${sectionId}/learning-plan${qs}`);
  }

  static async getSessionPlan(sessionPlanId: string): Promise<{
    id: string;
    status: "pending" | "ready" | "failed" | "expired";
    plan?: any;
    currentGainIndex?: number;
    history?: Array<{ role: "user" | "assistant"; content: string }>;
    errorMessage?: string;
  }> {
    return this.getApi(`ai/session-plan/${sessionPlanId}`);
  }

  static async updateSessionPlanProgress(
    sessionPlanId: string,
    payload: { currentGainIndex?: number; history?: Array<{ role: "user" | "assistant"; content: string }> },
  ): Promise<{ id: string; currentGainIndex: number; historyLength: number }> {
    return this.patchApi(`ai/session-plan/${sessionPlanId}/progress`, payload);
  }

  static async resetSessionPlan(
    sessionPlanId: string,
  ): Promise<{ id: string; currentGainIndex: number; historyLength: number }> {
    return this.postApi(`ai/session-plan/${sessionPlanId}/reset`, {});
  }

  static async generateAITeacherSpeech(text: string): Promise<Blob> {
    const token = localStorage.getItem("authToken");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/ai/tutor/ai-teacher/speech`, {
      method: "POST",
      headers,
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const details = errorData.details ? `: ${errorData.details}` : "";
      throw new Error(`${errorData.message || `HTTP Hatası! Durum: ${response.status}`}${details}`);
    }

    return response.blob();
  }

  static async streamAITeacherSpeech(
    text: string,
    handlers: {
      onStart?: (metadata: { mode: string; sampleRate: number; channels: number; bitDepth: number; model?: string | null }) => void;
      onChunk?: (chunk: Uint8Array, metadata: { sampleRate: number; channels: number; bitDepth: number }) => void;
      onEnd?: () => void;
    } = {},
  ): Promise<{ streamed: boolean; blob?: Blob; mode?: string }> {
    const token = localStorage.getItem("authToken");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/ai/tutor/ai-teacher/speech/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const details = errorData.details ? `: ${errorData.details}` : "";
      throw new Error(`${errorData.message || `HTTP Hatası! Durum: ${response.status}`}${details}`);
    }

    const mode = response.headers.get("X-AI-Teacher-Audio-Mode") || "";
    const contentType = response.headers.get("content-type") || "";
    const isLivePcmStream = mode === "live" || /audio\/(L16|pcm)/i.test(contentType);
    if (!isLivePcmStream || !response.body) {
      return { streamed: false, blob: await response.blob(), mode };
    }

    const rateMatch = contentType.match(/rate=(\d+)/i);
    const sampleRate = Number(response.headers.get("X-Audio-Sample-Rate")) || Number(rateMatch?.[1]) || 24000;
    const channels = Number(response.headers.get("X-Audio-Channels")) || 1;
    const bitDepth = Number(response.headers.get("X-Audio-Bit-Depth")) || 16;
    const model = response.headers.get("X-TTS-Model");

    handlers.onStart?.({ mode, sampleRate, channels, bitDepth, model });

    const reader = response.body.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value?.byteLength) handlers.onChunk?.(value, { sampleRate, channels, bitDepth });
    }

    handlers.onEnd?.();
    return { streamed: true, mode };
  }

  public static async getSurveys(filters?: SurveyFilters): Promise<SurveyResponse> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.search) params.append("search", filters.search);
    if (filters?.isActive !== undefined) params.append("isActive", filters.isActive.toString());
    if (filters?.type) params.append("type", filters.type);

    const queryString = params.toString();
    const endpoint = `/surveys${queryString ? `?${queryString}` : ""}`;

    return this.request<SurveyResponse>(endpoint);
  }

  public static async getSurveyPreview(surveyId: string): Promise<any> {
    return this.getApi(`surveys/preview/${surveyId}`);
  }

  public static async getSurvey(surveyId: string): Promise<any> {
    return this.getApi(`surveys/${surveyId}`);
  }

  public static async createSurvey(surveyData: any): Promise<any> {
    return this.postApi("surveys", surveyData);
  }

  public static async updateSurvey(surveyId: string, surveyData: any): Promise<any> {
    return this.putApi(`surveys/${surveyId}`, surveyData);
  }

  public static async deleteSurvey(surveyId: string): Promise<void> {
    return this.deleteApi(`surveys/${surveyId}`);
  }

  public static async getSurveyParticipants(surveyId: string, page?: number, limit?: number): Promise<any> {
    const params = new URLSearchParams();
    if (page !== undefined) params.set("page", String(page));
    if (limit !== undefined) params.set("limit", String(limit));
    const query = params.toString();

    return this.request<any>(`/surveys/${surveyId}/participants${query ? `?${query}` : ""}`);
  }

  public static async createSurveyParticipant(surveyId: string, participantData: any): Promise<any> {
    return this.postApi(`surveys/${surveyId}/participants`, participantData);
  }

  public static async deleteSurveyParticipant(surveyId: string, participantId: string): Promise<void> {
    return this.deleteApi(`surveys/${surveyId}/participants/${participantId}`);
  }

  public static async startSurveyParticipation(surveyId: string): Promise<any> {
    return this.postApi(`surveys/${surveyId}/start`, {});
  }

  public static async submitSurveyAnswer(
    surveyId: string,
    answerData: { surveyQuestionId: string; answer: any; answerMediaId?: string }
  ): Promise<any> {
    return this.postApi(`surveys/${surveyId}/answer`, answerData);
  }

  public static async uploadSurveyAnswerFile(
    surveyId: string,
    file: File
  ): Promise<{ id: string; url: string; name: string; mimeType: string; size: number }> {
    const formData = new FormData();
    formData.append("file", file);
    return this.request(`/surveys/${surveyId}/upload-answer-file`, {
      method: "POST",
      body: formData,
    });
  }

  public static async submitSurveyAnswers(
    surveyId: string,
    answersData: { answers: Array<{ surveyQuestionId: string; answer: any; answerMediaId?: string }> }
  ): Promise<any> {
    return this.postApi(`surveys/${surveyId}/answers`, answersData);
  }

  public static async completeSurvey(surveyId: string): Promise<any> {
    return this.postApi(`surveys/${surveyId}/complete`, {});
  }

  public static async submitSurveyResponse(surveyId: string, answers: Array<{ questionId: string; answer: any }>): Promise<any> {
    const formattedAnswers = answers.map((answer) => ({
      surveyQuestionId: answer.questionId,
      answer: answer.answer,
    }));

    await this.submitSurveyAnswers(surveyId, { answers: formattedAnswers });
    return this.completeSurvey(surveyId);
  }

  public static async getSurveyResults(surveyId: string): Promise<any> {
    return this.getApi(`surveys/${surveyId}/results`);
  }

  public static async getSurveyParticipantDepartmentCounts(surveyId: string): Promise<Record<string, number>> {
    return this.getApi(`surveys/${surveyId}/results/participant-department-counts`);
  }

  public static async getMySurveyAnswers(surveyId: string): Promise<any> {
    return this.getApi(`surveys/${surveyId}/my-answers`);
  }

  public static async getSurveyReviewDepartments(surveyId: string): Promise<Array<{ id: string; name: string; color?: string; parentId?: string | null }>> {
    return this.getApi(`surveys/${surveyId}/review-departments`);
  }

  public static async getMySurveyReviewDepartments(): Promise<Array<{ id: string; name: string; color?: string; parentId?: string | null }>> {
    return this.getApi("surveys/review-departments/me");
  }

  public static async reviewSurveyResponse(
    surveyId: string,
    targetUserId: string,
    payload: { decision: "APPROVE" | "REJECT"; departmentId?: string | null }
  ): Promise<any> {
    return this.postApi(`surveys/${surveyId}/review/${targetUserId}`, payload);
  }

  public static async resetSurveyResponse(surveyId: string, targetUserId: string): Promise<any> {
    return this.deleteApi(`surveys/${surveyId}/responses/${targetUserId}`);
  }

  // ----------------------------------- İLMEK ----------------------------------
  // İçerik Paketi (öğretmen/yönetici tarafı)
  public static async getIlmekContentPacks(
    filters?: IlmekContentPackFilters
  ): Promise<IlmekContentPackListResponse> {
    const query = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          query.append(key, String(value));
        }
      });
    }
    const qs = query.toString();
    return this.request<IlmekContentPackListResponse>(
      `/ilmek/content-packs${qs ? `?${qs}` : ""}`
    );
  }

  public static async getIlmekContentPack(packId: string): Promise<IlmekContentPack> {
    return this.getApi(`ilmek/content-packs/${packId}`);
  }

  public static async getIlmekApprovedPack(kazanimKodu: string): Promise<IlmekContentPack> {
    return this.getApi(`ilmek/content-packs/by-kazanim/${encodeURIComponent(kazanimKodu)}`);
  }

  public static async createIlmekContentPack(
    payload: Partial<IlmekContentPack>
  ): Promise<IlmekContentPack> {
    return this.postApi("ilmek/content-packs", payload);
  }

  public static async generateIlmekContentPack(
    payload: { kazanimKodu: string; ders?: string; sinif?: number; unite?: string; baslik?: string }
  ): Promise<IlmekContentPack> {
    return this.postApi("ilmek/content-packs/generate", payload);
  }

  public static async updateIlmekContentPack(
    packId: string,
    payload: Partial<IlmekContentPack>
  ): Promise<IlmekContentPack> {
    return this.putApi(`ilmek/content-packs/${packId}`, payload);
  }

  public static async approveIlmekContentPack(
    packId: string,
    action: "APPROVE" | "REJECT",
    not?: string
  ): Promise<IlmekContentPack> {
    return this.postApi(`ilmek/content-packs/${packId}/approve`, { action, not });
  }

  public static async deleteIlmekContentPack(packId: string): Promise<void> {
    return this.deleteApi(`ilmek/content-packs/${packId}`);
  }

  // Journey (öğrenci tarafı)
  public static async createIlmekJourney(
    kazanimKodu: string,
    ogrenciId?: string
  ): Promise<IlmekJourney> {
    return this.postApi("ilmek/journeys", { kazanimKodu, ogrenciId });
  }

  public static async getIlmekJourney(journeyId: string): Promise<IlmekJourney> {
    return this.getApi(`ilmek/journeys/${journeyId}`);
  }

  public static async ilmekTurn(journeyId: string, ilmek: string, girdi: string): Promise<IlmekTurnResult> {
    return this.postApi(`ilmek/journeys/${journeyId}/turn`, { ilmek, girdi });
  }

  public static async advanceIlmekJourney(journeyId: string): Promise<IlmekJourney> {
    return this.postApi(`ilmek/journeys/${journeyId}/advance`, {});
  }

  public static async exportSurveyResults(surveyId: string, departmentIds?: string[]): Promise<Blob> {
    const query = new URLSearchParams();

    (departmentIds || [])
      .map((departmentId) => String(departmentId || "").trim())
      .filter(Boolean)
      .forEach((departmentId) => query.append("departmentIds", departmentId));

    const endpoint = `${API_BASE_URL}/surveys/${surveyId}/results/export${query.toString() ? `?${query.toString()}` : ""}`;
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;

      throw new Error(errorMessage);
    };

    return response.blob();
  }

  public static async getSurveySections(surveyId: string): Promise<any> {
    return this.request<any>(`/surveys/${surveyId}/sections`);
  }

  public static async createSurveySection(surveyId: string, sectionData: any): Promise<any> {
    return this.postApi(`surveys/${surveyId}/sections`, sectionData);
  }

  public static async updateSurveySection(surveyId: string, sectionId: string, sectionData: any): Promise<any> {
    return this.putApi(`surveys/${surveyId}/sections/${sectionId}`, sectionData);
  }

  public static async deleteSurveySection(surveyId: string, sectionId: string): Promise<void> {
    return this.deleteApi(`surveys/${surveyId}/sections/${sectionId}`);
  }

  public static async getSurveyQuestions(surveyId: string, sectionId: string): Promise<any> {
    return this.request<any>(`/surveys/${surveyId}/sections/${sectionId}/questions`);
  }

  public static async createSurveyQuestion(surveyId: string, sectionId: string, questionData: any): Promise<any> {
    return this.postApi(`surveys/${surveyId}/sections/${sectionId}/questions`, questionData);
  }

  public static async updateSurveyQuestion(surveyId: string, sectionId: string, questionId: string, questionData: any): Promise<any> {
    return this.putApi(`surveys/${surveyId}/sections/${sectionId}/questions/${questionId}`, questionData);
  }

  public static async deleteSurveyQuestion(surveyId: string, sectionId: string, questionId: string): Promise<void> {
    return this.deleteApi(`surveys/${surveyId}/sections/${sectionId}/questions/${questionId}`);
  }

  public static async getUsers(page = 1, limit?: number, search?: string, departmentId?: string): Promise<any> {
    let url = `/users?page=${page}`;
    if (limit !== undefined) {
      url += `&limit=${limit}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (departmentId) {
      url += `&departmentId=${encodeURIComponent(departmentId)}`;
    }
    return this.request<any>(url);
  }

  public static async getDepartments(page = 1, limit?: number, additionalParams?: string): Promise<any> {
    let url = `/departments?page=${page}`;
    if (limit !== undefined) {
      url += `&limit=${limit}`;
    }
    if (additionalParams) {
      url += additionalParams.startsWith("&") ? additionalParams : `&${additionalParams}`;
    }
    return this.request<any>(url);
  }

  public static async getAllDepartments(parentId?: string | null): Promise<any> {
    let url = "departments/all";
    if (parentId !== undefined && parentId !== null) {
      url += `?parentId=${parentId}`;
    }
    return this.getApi(url);
  }

  public static async getDepartment(departmentId: string): Promise<any> {
    return this.getApi(`departments/${departmentId}`);
  }

  public static async searchDepartments(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    parentId?: string | null;
    search?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params?.parentId !== undefined && params?.parentId !== null) {
      queryParams.append("parentId", params.parentId);
    }
    if (params?.search) queryParams.append("search", params.search);

    const query = queryParams.toString();
    return this.request<any>(`/departments/search${query ? `?${query}` : ""}`);
  }

  public static async searchAllDepartments(params?: {
    sortBy?: string;
    sortOrder?: string;
    parentId?: string | null;
    search?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params?.parentId !== undefined && params?.parentId !== null) {
      queryParams.append("parentId", params.parentId);
    }
    if (params?.search) queryParams.append("search", params.search);

    const query = queryParams.toString();
    return this.request<any>(`/departments/search/all${query ? `?${query}` : ""}`);
  }

  public static async getLessonCalendars(lessonId: string, week?: number): Promise<any> {
    try {
      let url = `/academic/${lessonId}/calendars`;
      if (week) {
        url += `?week=${week}`;
      }
      return this.request<any>(url);
    } catch (error) {
      return { data: [] };
    }
  }

  public static async createLessonCalendar(
    lessonId: string,
    calendarData: {
      week: number;
      departmentId: string;
      sectionId: string;
      contentId: string;
    }
  ): Promise<any> {
    try {
      return this.postApi(`academic/${lessonId}/calendars`, calendarData);
    } catch (error) {
      throw new Error("Takvim Öğesi Ekleme Özelliği Henüz Hazır Değil");
    }
  }

  public static async deleteLessonCalendar(lessonId: string, calendarId: string): Promise<void> {
    try {
      return this.deleteApi(`academic/${lessonId}/calendars/${calendarId}`);
    } catch (error) {
      throw new Error("Takvim Öğesi Silme Özelliği Henüz Hazır Değil");
    }
  }

  public static async deleteLessonCalendarItem(lessonId: string, calendarId: string, itemId: string): Promise<void> {
    try {
      return this.deleteApi(`academic/${lessonId}/calendars/${calendarId}/${itemId}`);
    } catch (error) {
      throw new Error("Takvim Öğesi Silme Özelliği Henüz Hazır Değil");
    }
  }

  public static async sendWeekHomeWork(body: any): Promise<any> {
    return this.postApi("academic/homeworks", body);
  }

  static async createLessonSection(lessonId: string, data: any): Promise<any> {
    return this.postApi(`lessons/${lessonId}/sections`, data);
  }

  static async updateLessonSection(lessonId: string, sectionId: string, data: any): Promise<any> {
    return this.putApi(`lessons/${lessonId}/sections/${sectionId}`, data);
  }

  static async deleteLessonSection(lessonId: string, sectionId: string): Promise<any> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}`);
  }

  static async createSectionContent(lessonId: string, sectionId: string, data: any): Promise<any> {
    return this.postApi(`lessons/${lessonId}/sections/${sectionId}/contents`, data);
  }

  static async updateSectionContent(lessonId: string, sectionId: string, contentId: string, data: any): Promise<any> {
    return this.putApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}`, data);
  }

  static async deleteSectionContent(lessonId: string, sectionId: string, contentId: string): Promise<any> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}`);
  }

  public static async getHomeworks(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<BaseResponse> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(Math.min(params.limit, 20)));
    if (params?.search) qs.append("search", params.search);
    if (params?.status) qs.append("status", params.status);
    if (params?.startDate) qs.append("startDate", params.startDate);
    if (params?.endDate) qs.append("endDate", params.endDate);
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<BaseResponse>(`/homeworks${query ? `?${query}` : ""}`);
  }

  public static async getLessonHomeworks(
    lessonId: string,
    params?: {
      status?: string;
      lessonSectionId?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: string;
      limit?: number;
      page?: number;
    }
  ): Promise<any> {
    const query = new URLSearchParams();
    if (params?.status) query.append("status", params.status);
    if (params?.lessonSectionId) query.append("lessonSectionId", params.lessonSectionId);
    if (params?.search) query.append("search", params.search);
    if (params?.sortBy) query.append("sortBy", params.sortBy);
    if (params?.sortOrder) query.append("sortOrder", params.sortOrder);
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.page) query.append("page", params.page.toString());

    return this.request<any>(`/homeworks/${lessonId}${query.toString() ? `?${query.toString()}` : ""}`);
  }

  public static async getLessonQAs(
    lessonId: string,
    params?: {
      sectionId?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.sectionId) qs.append("sectionId", params.sectionId);
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/lessons/${lessonId}/qa${query ? `?${query}` : ""}`);
  }

  public static async createLessonQA(
    lessonId: string,
    data: {
      sectionId?: string;
      parentId?: string | null;
      question: string;
      metadata?: any;
    }
  ): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/qa`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  public static async deleteLessonQA(lessonId: string, qaId: string): Promise<void> {
    return this.request<void>(`/lessons/${lessonId}/qa/${qaId}`, {
      method: "DELETE",
    });
  }

  public static async getLessonVotes(
    lessonId: string,
    params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/lessons/${lessonId}/votes${query ? `?${query}` : ""}`);
  }

  public static async createLessonVote(lessonId: string, data: { vote: number }): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/votes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  public static async getLessonNotes(
    lessonId: string,
    params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/lessons/${lessonId}/notes${query ? `?${query}` : ""}`);
  }

  public static async createLessonNote(lessonId: string, data: { content: string; sectionId?: string; metadata?: any }): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/notes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  public static async deleteLessonNote(lessonId: string, noteId: string): Promise<void> {
    return this.request<void>(`/lessons/${lessonId}/notes/${noteId}`, {
      method: "DELETE",
    });
  }

  public static async getLessonComments(
    lessonId: string,
    params?: {
      sectionId?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.sectionId) qs.append("sectionId", params.sectionId);
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/lessons/${lessonId}/comments${query ? `?${query}` : ""}`);
  }

  public static async createLessonComment(lessonId: string, data: { content: string; sectionId?: string; metadata?: any }): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/comments`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  public static async deleteLessonComment(lessonId: string, commentId: string): Promise<void> {
    return this.request<void>(`/lessons/${lessonId}/comments/${commentId}`, {
      method: "DELETE",
    });
  }

  public static async getMeetings(filters?: MeetingFilters): Promise<MeetingResponse> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.search) params.append("search", filters.search);
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);

    const queryString = params.toString();
    const endpoint = `/meets${queryString ? `?${queryString}` : ""}`;

    return this.request<MeetingResponse>(endpoint);
  }

  public static async getMeeting(meetingId: string): Promise<MeetingDetailResponse> {
    return this.getApi(`meets/${meetingId}`);
  }

  public static async joinMeeting(meetingId: string): Promise<JoinMeetingResponse> {
    return this.getApi(`meets/${meetingId}/join`);
  }

  public static async createMeeting(meetingData: CreateMeetingData): Promise<any> {
    return this.postApi("meets", meetingData);
  }

  public static async updateMeeting(meetingId: string, meetingData: UpdateMeetingData): Promise<any> {
    return this.putApi(`meets/${meetingId}`, meetingData);
  }

  public static async deleteMeeting(meetingId: string): Promise<void> {
    return this.deleteApi(`meets/${meetingId}`);
  }

  public static async getMeetingParticipants(meetingId: string, page = 1, limit = 10): Promise<MeetingParticipantsResponse> {
    return this.request<MeetingParticipantsResponse>(`/meets/${meetingId}/participants?page=${page}&limit=${limit}`);
  }

  public static async createMeetingParticipant(meetingId: string, participantData: CreateParticipantData): Promise<any> {
    return this.request<any>(`/meets/${meetingId}/participants`, {
      method: "POST",
      body: JSON.stringify(participantData),
    });
  }

  public static async deleteMeetingParticipant(meetingId: string, participantId: string): Promise<void> {
    return this.request<void>(`/meets/${meetingId}/participants/${participantId}`, {
      method: "DELETE",
    });
  }

  public static async getMeetingSummary(meetingId: string): Promise<any> {
    return this.getApi(`meets/${meetingId}/summary`);
  }

  public static async updateMeetingSummary(
    meetingId: string,
    summaryData: {
      title?: string | null;
      overview?: string | null;
      content?: string | null;
      details?: any;
      nextSteps?: any;
      show?: boolean;
    }
  ): Promise<any> {
    return this.putApi(`meets/${meetingId}/summary`, summaryData);
  }

  public static async getMeetingAttendance(meetingId: string): Promise<{ success: boolean; data: MeetAttendanceRecord[] }> {
    const resp = await this.getApi(`meets/${meetingId}/attendance`);
    let raw: any = resp;
    if (resp && typeof resp === "object" && !Array.isArray(resp) && "data" in resp) {
      raw = (resp as any).data;
    }
    const arr = Array.isArray(raw) ? raw : [];
    const mapped: MeetAttendanceRecord[] = arr.map((item: any) => {
      const firstName = item.firstName || item.user?.firstName || "";
      const lastName = item.lastName || item.user?.lastName || "";
      const participantName = `${firstName} ${lastName}`.trim();
      const durationSec =
        typeof item.duration === "number" ? item.duration : typeof item.totalDuration === "number" ? item.totalDuration * 60 : 0;
      const totalDuration = Math.floor(durationSec / 60);
      return {
        id: item.id || `meet_attendance_ui_${item.userId || Math.random().toString(36).slice(2)}`,
        meetId: meetingId,
        zoomMeetingId: item.zoomMeetingId || "",
        userId: item.userId || item.user?.id || "",
        participantName,
        normalizedName: participantName.toUpperCase(),
        totalDuration,
        isPresent: item.joined === true || item.isPresent === true,
        joinDetails: item.joinDetails || [],
        processedAt: item.processedAt || item.updatedAt || item.createdAt || new Date().toISOString(),
        metadata: {
          departmentId: (item.department && item.department.id) || item.departmentId || item.user?.departmentId,
          department: item.department || undefined,
          source: "simple_attendance_endpoint",
        },
        user:
          item.user ||
          (item.userId
            ? {
              id: item.userId,
              firstName,
              lastName,
              email: item.email || "",
              departmentId: (item.department && item.department.id) || item.departmentId,
              media: item.media || undefined,
            }
            : undefined),
      };
    });
    return { success: true, data: mapped };
  }

  public static async getQuestionBanks(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<QuestionBankListResponse> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<QuestionBankListResponse>(`/question-banks${query ? `?${query}` : ""}`);
  }

  public static async createQuestionBank(payload: CreateQuestionBankPayload): Promise<QuestionBankDraft> {
    return this.postApi("question-banks", payload);
  }

  public static async getQuestionBankShareCandidates(params?: {
    search?: string;
    limit?: number;
  }): Promise<QuestionBankShareCandidate[]> {
    const qs = new URLSearchParams();
    if (params?.search) qs.append("search", params.search);
    if (params?.limit) qs.append("limit", String(params.limit));
    const query = qs.toString();
    return this.getApi(`question-banks/share-candidates${query ? `?${query}` : ""}`);
  }

  public static async shareQuestionBank(
    id: string,
    payload: {
      recipientUserId: string;
      allowEditing: boolean;
    }
  ): Promise<QuestionBankDraft> {
    return this.postApi(`question-banks/${id}/share`, payload);
  }

  public static async getQuestionBank(id: string): Promise<QuestionBankDraft> {
    return this.getApi(`question-banks/${id}`);
  }

  public static async updateQuestionBank(
    id: string,
    payload: UpdateQuestionBankPayload,
    options: Pick<RequestInit, "keepalive"> = {}
  ): Promise<QuestionBankDraft> {
    return this.request<QuestionBankDraft>(`/question-banks/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
      ...options,
    });
  }

  public static async deleteQuestionBank(id: string): Promise<void> {
    return this.deleteApi(`question-banks/${id}`);
  }

  public static async downloadQuestionBank(id: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/question-banks/${id}/download`);
  }

  // Çözümlü Test PDF (KaTeX render'lı kontrol kopyası: sorular + cevap anahtarı + çözüm)
  public static async downloadQuestionBankPdf(id: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/question-banks/${id}/pdf`);
  }

  public static async generateMaarifTest(payload: MaarifGeneratePayload): Promise<MaarifGeneratedPreview> {
    const formData = new FormData();
    formData.append("testAdi", payload.testAdi);
    formData.append("lessonId", payload.lessonId);
    formData.append("sectionIds", JSON.stringify(payload.sectionIds || []));
    formData.append("selectedContentIds", JSON.stringify(payload.selectedContentIds || []));
    formData.append("gradeLabel", payload.gradeLabel);
    formData.append("topic", payload.topic || "");
    formData.append("difficulty", payload.difficulty);
    formData.append("questionCount", String(payload.questionCount));
    formData.append("choiceCount", String(payload.choiceCount));
    formData.append("imageQuestionCount", String(payload.imageQuestionCount));
    if (payload.pdf) {
      formData.append("pdf", payload.pdf);
    }

    return this.request<MaarifGeneratedPreview>("/maarif-test-generator/generate", {
      method: "POST",
      body: formData,
    });
  }

  public static async saveMaarifTest(payload: MaarifSavePayload): Promise<MaarifSaveResponse> {
    return this.postApi("maarif-test-generator/save", payload);
  }

  public static async listMaarifTests(): Promise<FileListResponse> {
    return this.getApi("maarif-test-generator/list");
  }

  public static async generateMaarifQuestionContext(
    payload: MaarifQuestionContextPayload
  ): Promise<MaarifQuestionContextResponse> {
    return this.postApi("maarif-question-wizard/context", payload);
  }

  // --- Curriculum (MEB Maarif modeli) lookup ---
  public static async getCurriculumCourses(): Promise<Array<{ code: string; name: string }>> {
    return this.getApi("curriculum/courses");
  }
  public static async getCurriculumGrades(courseCode: string): Promise<number[]> {
    return this.getApi(`curriculum/courses/${encodeURIComponent(courseCode)}/grades`);
  }
  public static async getCurriculumUnits(courseCode: string, grade: number): Promise<any[]> {
    return this.getApi(`curriculum/courses/${encodeURIComponent(courseCode)}/grades/${grade}/units`);
  }
  public static async getCurriculumUnit(unitCode: string): Promise<any> {
    return this.getApi(`curriculum/units/${encodeURIComponent(unitCode)}`);
  }
  public static async getCurriculumEnrichedOutcome(outcomeCode: string, processLetter?: string | null): Promise<any> {
    const qs = processLetter ? `?processLetter=${encodeURIComponent(processLetter)}` : "";
    return this.getApi(`curriculum/outcomes/${encodeURIComponent(outcomeCode)}/enriched${qs}`);
  }
  public static async getCurriculumUnitHealth(unitCode: string): Promise<import("../types/curriculum").CurriculumUnitHealth> {
    return this.getApi(`curriculum/units/${encodeURIComponent(unitCode)}/health`);
  }

  // --- Maarif Batch (toplu soru üretimi) ---
  public static async previewMaarifBatch(payload: {
    unitCode: string;
    params?: import("../types/maarif-batch").MaarifBatchParams;
  }): Promise<import("../types/maarif-batch").MaarifBatchPreview> {
    return this.postApi("maarif-batch/preview", payload);
  }
  public static async createMaarifBatch(payload: {
    unitCode: string;
    params?: import("../types/maarif-batch").MaarifBatchParams;
    selectedTests?: { konuKey: string; testIndex: number }[];
  }): Promise<import("../types/maarif-batch").MaarifBatchJob> {
    return this.postApi("maarif-batch", payload);
  }
  public static async listMaarifBatchJobs(query?: {
    page?: number;
    limit?: number;
    unitCode?: string;
    status?: string;
  }): Promise<import("../types/maarif-batch").MaarifBatchJobListResponse> {
    const qs = new URLSearchParams(
      Object.entries(query || {}).filter(([, v]) => v !== undefined && v !== "") as [string, string][]
    ).toString();
    return this.getApi(`maarif-batch${qs ? `?${qs}` : ""}`);
  }
  public static async getMaarifBatchJob(id: string): Promise<import("../types/maarif-batch").MaarifBatchJob> {
    return this.getApi(`maarif-batch/${encodeURIComponent(id)}`);
  }
  public static async getMaarifBatchJobSteps(
    id: string,
    query?: { status?: string; konuKey?: string }
  ): Promise<{ data: import("../types/maarif-batch").MaarifBatchStep[] }> {
    const qs = new URLSearchParams(
      Object.entries(query || {}).filter(([, v]) => v !== undefined && v !== "") as [string, string][]
    ).toString();
    return this.getApi(`maarif-batch/${encodeURIComponent(id)}/steps${qs ? `?${qs}` : ""}`);
  }
  public static async resumeMaarifBatchJob(id: string): Promise<import("../types/maarif-batch").MaarifBatchJob> {
    return this.postApi(`maarif-batch/${encodeURIComponent(id)}/resume`, {});
  }
  public static async cancelMaarifBatchJob(id: string): Promise<import("../types/maarif-batch").MaarifBatchJob> {
    return this.postApi(`maarif-batch/${encodeURIComponent(id)}/cancel`, {});
  }

  // --- Maarif V2 (curriculum-based) ---
  public static async generateMaarifContextV2(payload: any): Promise<any> {
    return this.postApi("maarif-question-wizard/v2/context", payload);
  }
  public static async generateMaarifQuestionV2(payload: any): Promise<any> {
    return this.postApi("maarif-question-wizard/v2/question", payload);
  }
  public static async generateMaarifQuestionsFromContextV2(payload: {
    outcomeCode: string; context: string; count?: number; difficulty?: string; customPrompt?: string | null; wantsVisual?: boolean; withJudge?: boolean;
  }): Promise<any> {
    return this.postApi("maarif-question-wizard/v2/questions-from-context", payload);
  }

  public static async generateMaarifSingleQuestion(
    payload: MaarifQuestionGeneratePayload
  ): Promise<MaarifQuestionGenerateResponse> {
    return this.postApi("maarif-question-wizard/question", payload);
  }

  public static async generateMaarifQuestionFromImage(
    payload: MaarifQuestionGenerateFromImagePayload
  ): Promise<MaarifQuestionGenerateFromImageResponse> {
    const formData = new FormData();
    formData.append("lessonId", payload.lessonId);
    formData.append("gradeLabel", payload.gradeLabel);
    formData.append("difficulty", payload.difficulty);
    formData.append("lengthPreset", payload.lengthPreset);
    formData.append("wantsVisual", String(Boolean(payload.wantsVisual)));
    if (payload.targetWordCount) {
      formData.append("targetWordCount", String(payload.targetWordCount));
    }
    formData.append("customPrompt", payload.customPrompt || "");
    formData.append("gainText", payload.gainText);
    formData.append("processComponentCode", payload.processComponentCode);
    formData.append("context", payload.context || "");
    formData.append("image", payload.image);

    return this.request<MaarifQuestionGenerateFromImageResponse>("/maarif-question-wizard/question-from-image", {
      method: "POST",
      body: formData,
    });
  }

  public static async getMaarifQuestionPdfMetadata(pdf: File): Promise<MaarifQuestionPdfMetadataResponse> {
    const formData = new FormData();
    formData.append("pdf", pdf);

    return this.request<MaarifQuestionPdfMetadataResponse>("/maarif-question-wizard/pdf-metadata", {
      method: "POST",
      body: formData,
    });
  }

  public static async getMaarifQuestionPdfPreviews(
    pdf: File,
    selectedPages: number[]
  ): Promise<MaarifQuestionPdfPreviewsResponse> {
    const formData = new FormData();
    formData.append("pdf", pdf);
    formData.append("selectedPages", JSON.stringify(selectedPages || []));

    return this.request<MaarifQuestionPdfPreviewsResponse>("/maarif-question-wizard/pdf-previews", {
      method: "POST",
      body: formData,
    });
  }

  public static async startMaarifQuestionsFromPdfJob(
    payload: MaarifQuestionGenerateFromPdfPayload
  ): Promise<MaarifQuestionPdfJobResponse> {
    const formData = new FormData();
    formData.append("difficulty", payload.difficulty);
    formData.append("rewriteMode", payload.rewriteMode);
    formData.append("lengthPreset", payload.lengthPreset);
    formData.append("wantsVisual", String(Boolean(payload.wantsVisual)));
    if (payload.targetWordCount) {
      formData.append("targetWordCount", String(payload.targetWordCount));
    }
    formData.append("customPrompt", payload.customPrompt || "");
    formData.append("context", payload.context || "");
    formData.append("selectedPages", JSON.stringify(payload.selectedPages || []));
    formData.append("pdf", payload.pdf);

    return this.request<MaarifQuestionPdfJobResponse>("/maarif-question-wizard/questions-from-pdf", {
      method: "POST",
      body: formData,
    });
  }

  public static async getMaarifQuestionsFromPdfJobStatus(
    jobId: string
  ): Promise<MaarifQuestionPdfJobResponse> {
    return this.request<MaarifQuestionPdfJobResponse>(`/maarif-question-wizard/questions-from-pdf/jobs/${jobId}`);
  }

  public static async auditMaarifQuestion(
    payload: MaarifQuestionAuditPayload
  ): Promise<MaarifQuestionAuditResponse> {
    const formData = new FormData();
    formData.append("lessonId", payload.lessonId);
    formData.append("gradeLabel", payload.gradeLabel);
    formData.append("difficulty", payload.difficulty);
    formData.append("lengthPreset", payload.lengthPreset);
    formData.append("wantsVisual", String(Boolean(payload.wantsVisual)));
    if (payload.targetWordCount) {
      formData.append("targetWordCount", String(payload.targetWordCount));
    }
    formData.append("customPrompt", payload.customPrompt || "");
    formData.append("gainText", payload.gainText);
    formData.append("processComponentCode", payload.processComponentCode);
    formData.append("questionText", payload.questionText || "");
    if (payload.image) {
      formData.append("image", payload.image);
    }

    return this.request<MaarifQuestionAuditResponse>("/maarif-question-wizard/audit", {
      method: "POST",
      body: formData,
    });
  }

  public static async rewriteMaarifQuestion(
    payload: MaarifQuestionRewritePayload
  ): Promise<MaarifQuestionRewriteResponse> {
    const formData = new FormData();
    formData.append("lessonId", payload.lessonId);
    formData.append("gradeLabel", payload.gradeLabel);
    formData.append("difficulty", payload.difficulty);
    formData.append("lengthPreset", payload.lengthPreset);
    formData.append("wantsVisual", String(Boolean(payload.wantsVisual)));
    if (payload.targetWordCount) {
      formData.append("targetWordCount", String(payload.targetWordCount));
    }
    formData.append("customPrompt", payload.customPrompt || "");
    formData.append("gainText", payload.gainText);
    formData.append("processComponentCode", payload.processComponentCode);
    formData.append("questionText", payload.questionText || "");
    formData.append("context", payload.context || "");
    formData.append("auditReport", JSON.stringify(payload.auditReport));
    if (payload.image) {
      formData.append("image", payload.image);
    }

    return this.request<MaarifQuestionRewriteResponse>("/maarif-question-wizard/rewrite", {
      method: "POST",
      body: formData,
    });
  }

  public static async saveMaarifQuestionRecord(
    payload: MaarifQuestionRecordSavePayload
  ): Promise<MaarifQuestionRecord> {
    return this.postApi("maarif-question-wizard/save", payload);
  }

  public static async exportMaarifQuestionToQuestionBank(
    payload: MaarifQuestionToQuestionBankPayload
  ): Promise<MaarifQuestionToQuestionBankResponse> {
    return this.postApi("maarif-question-wizard/export-to-question-bank", payload);
  }

  public static async listMaarifExportTargets(limit = 10): Promise<MaarifExportTargetListResponse> {
    return this.getApi(`maarif-question-wizard/export-targets?limit=${limit}`);
  }

  public static async downloadMaarifQuestionV2Pdf(payload: {
    question: any;
    context?: string | null;
    enriched?: any;
    meta?: { outcomeCode?: string | null; processLetter?: string | null };
    judgeReport?: any;
    judgeAttempts?: any[] | null;
    judgeExhausted?: boolean;
  }): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob("/maarif-question-wizard/v2/export-question-pdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  public static async downloadMaarifQuestionGroupV2Pdf(payload: {
    questions: any[];
    context?: string | null;
    enriched?: any;
    meta?: { outcomeCode?: string | null; unitName?: string | null };
  }): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob("/maarif-question-wizard/v2/export-question-group-pdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  }

  public static async listMaarifQuestionRecords(): Promise<MaarifQuestionRecordListResponse> {
    return this.getApi("maarif-question-wizard/list");
  }

  public static async getMaarifQuestionRecord(id: string): Promise<MaarifQuestionRecord> {
    return this.getApi(`maarif-question-wizard/${id}`);
  }

  public static async updateMaarifQuestionRecord(
    id: string,
    payload: MaarifQuestionRecordUpdatePayload
  ): Promise<MaarifQuestionRecord> {
    return this.putApi(`maarif-question-wizard/${id}`, payload);
  }

  public static async deleteMaarifQuestionRecord(id: string): Promise<void> {
    return this.deleteApi(`maarif-question-wizard/${id}`);
  }

  public static async getMaarifPromptConfigs(): Promise<MaarifPromptConfig[]> {
    return this.getApi("maarif-question-wizard/admin/prompts");
  }

  public static async updateMaarifPromptConfig(key: string, content: string | null): Promise<MaarifPromptConfig[]> {
    return this.putApi(`maarif-question-wizard/admin/prompts/${key}`, { content });
  }

  public static async resetMaarifPromptConfig(key: string): Promise<MaarifPromptConfig[]> {
    return this.postApi(`maarif-question-wizard/admin/prompts/${key}/reset`, {});
  }

  public static async autoFillMeetingAttendance(meetingId: string): Promise<{ success: boolean; message: string; data: any }> {
    return this.postApi(`meets/attendance/meet/${meetingId}/auto-fill`, {});
  }

  public static async exportMeetingAttendance(meetingId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/meets/attendance/meet/${meetingId}/export`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;

      throw new Error(errorMessage);
    };

    return response.blob();
  }

  public static async getHomeworkReports(
    page = 1,
    limit = 20,
    sortBy = "createdAt",
    sortOrder = "DESC",
    filters?: {
      status?: string | null;
      type?: string | null;
      departmentId?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    }
  ): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    if (filters?.status) params.append("filter[status]", String(filters.status));
    if (filters?.type) params.append("filter[type]", String(filters.type));
    if (filters?.departmentId) params.append("filter[departmentId]", String(filters.departmentId));
    if (filters?.startDate) params.append("filter[startDate]", String(filters.startDate));
    if (filters?.endDate) params.append("filter[endDate]", String(filters.endDate));

    return this.request<any>(`/report/homeworks?${params.toString()}`);
  }

  public static async getHomeworkReportCounts(): Promise<any> {
    return this.getApi("report/homeworks/counts");
  }

  public static async exportHomeworkReports(filters?: {
    departmentId?: string;
    status?: string;
    type?: string;
    searchText?: string;
    homeworkTitle?: string;
    minScore?: number;
    maxScore?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.departmentId) params.append("filter[departmentId]", String(filters.departmentId));
      if (filters.status) params.append("filter[status]", String(filters.status));
      if (filters.type) params.append("filter[type]", String(filters.type));
      if (filters.searchText) params.append("filter[searchText]", String(filters.searchText));
      if (filters.homeworkTitle) params.append("filter[homeworkTitle]", String(filters.homeworkTitle));
      if (filters.minScore !== undefined) params.append("filter[minScore]", String(filters.minScore));
      if (filters.maxScore !== undefined) params.append("filter[maxScore]", String(filters.maxScore));
      if (filters.startDate) params.append("filter[startDate]", String(filters.startDate));
      if (filters.endDate) params.append("filter[endDate]", String(filters.endDate));
    }

    const url = `/report/homeworks/export${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  public static async getLessonSectionContentGains(lessonId: string, sectionId: string, contentId: string): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/gains`);
  }

  public static async getContentTranscript(contentId: string): Promise<{
    contentId: string;
    title: string;
    type: string;
    transcription: { status?: string; source?: string; segmentCount?: number; vimeoId?: string } | null;
    segmentCount: number;
    segments: Array<{ id: string; startTime: string; endTime: string; content: string }>;
  }> {
    return this.getApi(`lessons/contents/${contentId}/transcript`);
  }

  public static async createLessonSectionContentGain(
    lessonId: string,
    sectionId: string,
    contentId: string,
    gainData: {
      gain: string;
      startTime: string;
      endTime: string;
      metadata?: any;
    }
  ): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/gains`, {
      method: "POST",
      body: JSON.stringify(gainData),
    });
  }

  public static async updateLessonSectionContentGain(
    lessonId: string,
    sectionId: string,
    contentId: string,
    gainId: string,
    gainData: {
      gain: string;
      startTime: string;
      endTime: string;
      metadata?: any;
    }
  ): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/gains/${gainId}`, {
      method: "PUT",
      body: JSON.stringify(gainData),
    });
  }

  public static async deleteLessonSectionContentGain(
    lessonId: string,
    sectionId: string,
    contentId: string,
    gainId: string
  ): Promise<void> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/gains/${gainId}`);
  }

  public static async getLessonSectionContentQuestions(lessonId: string, sectionId: string, contentId: string): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions`);
  }

  public static async createLessonSectionContentQuestion(
    lessonId: string,
    sectionId: string,
    contentId: string,
    questionData: any
  ): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions`, {
      method: "POST",
      body: questionData,
    });
  }

  public static async updateLessonSectionContentQuestion(
    lessonId: string,
    sectionId: string,
    contentId: string,
    questionId: string,
    questionData: any
  ): Promise<any> {
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions/${questionId}`, {
      method: "PUT",
      body: questionData,
    });
  }

  public static async deleteLessonSectionContentQuestion(
    lessonId: string,
    sectionId: string,
    contentId: string,
    questionId: string
  ): Promise<void> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions/${questionId}`);
  }

  public static async deleteLessonSectionContentQuestionSolutionVideo(
    lessonId: string,
    sectionId: string,
    contentId: string,
    questionId: string
  ): Promise<any> {
    return this.deleteApi(`lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions/${questionId}/solution-video`);
  }

  public static async getExamQuestionOrderMap(lessonId: string, sectionId: string, contentId: string): Promise<Record<string, number>> {
    try {
      const response = await this.getLessonSectionContentQuestions(lessonId, sectionId, contentId);
      const raw = (response as any)?.data ?? (response as any) ?? [];
      const list = Array.isArray(raw) ? raw : [];

      const normalized = list.map((q: any) => ({
        id: q?.id || q?.qid || "",
        order: typeof q?.order === "number" ? q.order : 0,
        createdAt: q?.createdAt || "",
      }));

      const sorted = normalized.sort((a: any, b: any) => {
        if (a.order !== b.order) return a.order - b.order;
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (ta !== tb) return ta - tb;
        return String(a.id).localeCompare(String(b.id));
      });

      const map: Record<string, number> = {};
      sorted.forEach((q: any, idx: number) => {
        const key = String(q.id || "");
        if (key) map[key] = idx + 1;
      });
      return map;
    } catch (_) {
      return {};
    }
  }

  public static async bulkUpdateLessonSectionContentQuestions(
    lessonId: string,
    sectionId: string,
    contentId: string,
    files: File[]
  ): Promise<any> {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions/bulk-update`, {
      method: "POST",
      body: formData,
    });
  }

  public static async uploadAnswerKey(lessonId: string, sectionId: string, contentId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    return this.request<any>(`/lessons/${lessonId}/sections/${sectionId}/contents/${contentId}/questions/answer-key`, {
      method: "POST",
      body: formData,
    });
  }

  static async getAllLessons(): Promise<any> {
    return this.getApi("lessons/all");
  }

  public static async exportTestReports(filters: {
    lessonId: string;
    departmentIds: string[];
    topicId: string;
    testIds: string[];
    status?: number;
  }): Promise<Blob> {
    const endpoint = `/report/test/export`;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(filters),
    });
    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    }
    return response.blob();
  }

  public static async getTestReportDetail(testId: string, userId: string): Promise<any> {
    return this.getApi(`report/test/${testId}/user/${userId}`);
  }

  public static async getPlanningTeachers(params: {
    parentDepartmentId: string;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();
    qs.append("parentDepartmentId", params.parentDepartmentId);
    if (params.page) qs.append("page", String(params.page));
    if (params.limit) qs.append("limit", String(params.limit));
    if (params.search) qs.append("search", params.search);
    if (params.sortBy) qs.append("sortBy", params.sortBy);
    if (params.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/planning/available-teachers${query ? `?${query}` : ""}`);
  }

  public static async getPlanningStudents(params: {
    parentDepartmentId: string;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();
    qs.append("parentDepartmentId", params.parentDepartmentId);
    if (params.page) qs.append("page", String(params.page));
    if (params.limit) qs.append("limit", String(params.limit));
    if (params.search) qs.append("search", params.search);
    if (params.sortBy) qs.append("sortBy", params.sortBy);
    if (params.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/planning/available-students${query ? `?${query}` : ""}`);
  }

  public static async getPlanningLessons(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    isActive?: boolean;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    if (params?.isActive !== undefined) qs.append("isActive", String(params.isActive));

    const query = qs.toString();
    return this.request<any>(`/planning/lessons${query ? `?${query}` : ""}`);
  }

  public static async createPlanningLesson(data: {
    lessonName: string;
    lessonCode: string;
    lessonDuration?: number;
    isActive?: boolean;
  }): Promise<any> {
    return this.postApi("/planning/lessons", data);
  }

  public static async updatePlanningLesson(
    lessonId: string,
    data: {
      lessonName?: string;
      lessonCode?: string;
      lessonDuration?: number;
      isActive?: boolean;
    }
  ): Promise<any> {
    return this.putApi(`/planning/lessons/${lessonId}`, data);
  }

  public static async deletePlanningLesson(lessonId: string): Promise<any> {
    return this.deleteApi(`/planning/lessons/${lessonId}`);
  }

  public static async getPlanningClasses(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);

    const query = qs.toString();
    return this.request<any>(`/planning/classes${query ? `?${query}` : ""}`);
  }

  public static async getPlanningTimeTables(params?: { departmentId?: string }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.departmentId) qs.append("departmentId", params.departmentId);
    const query = qs.toString();
    return this.request<any>(`/planning/timetables${query ? `?${query}` : ""}`);
  }

  public static async updatePlanningTimeTables(data: {
    departmentId?: string;
    timeTables: Array<{
      period: number;
      startTime: string;
      endTime: string;
      day?: string;
    }>;
  }): Promise<any> {
    return this.postApi("planning/timetables", data);
  }

  public static async copyPlanningTimeTables(data: {
    sourceDepartmentId?: string;
    targetDepartmentId?: string;
    copyToAll?: boolean;
  }): Promise<any> {
    return this.postApi("planning/timetables/copy", data);
  }

  public static async getDepartmentDailyHours(params?: { departmentId?: string }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.departmentId) qs.append('departmentId', params.departmentId);
    const query = qs.toString();

    return this.request<any>(`/planning/department-daily-hours${query ? `?${query}` : ''}`);
  }

  public static async updateDepartmentDailyHours(data: {
    departmentId: string;
    days: Array<{
      dayOfWeek: string;
      startPeriod: number;
      endPeriod: number;
    }>;
  }): Promise<any> {
    return this.postApi('planning/department-daily-hours', data);
  }

  public static async bulkUpdateDepartmentDailyHours(data: {
    data: Array<{
      departmentId: string;
      days: Array<{
        dayOfWeek: string;
        startPeriod: number;
        endPeriod: number;
      }>;
    }>;
  }): Promise<any> {
    return this.postApi('planning/department-daily-hours/bulk', data);
  }

  public static async copyDepartmentDailyHours(data: {
    sourceDepartmentId: string;
    targetDepartmentId?: string;
    copyToAll?: boolean;
  }): Promise<any> {
    return this.postApi('planning/department-daily-hours/copy', data);
  }

  public static async getTeacherAssignments(params?: {
    page?: number;
    limit?: number;
    search?: string;
    teacherId?: string;
    lessonId?: string;
    classId?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.teacherId) qs.append("teacherId", params.teacherId);
    if (params?.lessonId) qs.append("lessonId", params.lessonId);
    if (params?.classId) qs.append("classId", params.classId);

    const query = qs.toString();
    return this.request<any>(`/planning/teacher-assignments${query ? `?${query}` : ""}`);
  }

  public static async getTeacherAssignmentTeachers(params?: {
    search?: string;
    lessonId?: string;
    teacherId?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.search) qs.append("search", params.search);
    if (params?.lessonId) qs.append("lessonId", params.lessonId);
    if (params?.teacherId) qs.append("teacherId", params.teacherId);

    const query = qs.toString();
    return this.request<any>(`/planning/teacher-assignments/teachers${query ? `?${query}` : ""}`);
  }

  public static async createTeacherAssignment(data: {
    teacherId: string;
    lessonId: string;
    classId: string;
  }): Promise<any> {
    return this.postApi("planning/teacher-assignments", data);
  }

  public static async deleteTeacherAssignment(id: string): Promise<any> {
    return this.deleteApi(`planning/teacher-assignments/${id}`);
  }

  public static async getTeacherBranchAssignments(params?: {
    page?: number;
    limit?: number;
    search?: string;
    teacherId?: string;
    lessonId?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.teacherId) qs.append("teacherId", params.teacherId);
    if (params?.lessonId) qs.append("lessonId", params.lessonId);

    const query = qs.toString();
    return this.request<any>(`/planning/teacher-branch-assignments${query ? `?${query}` : ""}`);
  }

  public static async getTeacherBranchAssignmentTeachers(params?: {
    search?: string;
    lessonId?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.search) qs.append("search", params.search);
    if (params?.lessonId) qs.append("lessonId", params.lessonId);

    const query = qs.toString();
    return this.request<any>(`/planning/teacher-branch-assignments/teachers${query ? `?${query}` : ""}`);
  }

  public static async createTeacherBranchAssignment(data: {
    teacherId: string;
    lessonId: string;
  }): Promise<any> {
    return this.postApi("planning/teacher-branch-assignments", data);
  }

  public static async deleteTeacherBranchAssignment(id: string): Promise<any> {
    return this.deleteApi(`planning/teacher-branch-assignments/${id}`);
  }

  public static async getClassLessonAssignments(classId: string): Promise<any> {
    return this.getApi(`planning/classes/${classId}/lessons`);
  }

  public static async upsertClassLessonAssignment(classId: string, lessonId: string, hours: number): Promise<any> {
    return this.putApi(`planning/classes/${classId}/lessons/${lessonId}`, { hours });
  }

  public static async deleteClassLessonAssignment(classId: string, lessonId: string): Promise<any> {
    return this.deleteApi(`planning/classes/${classId}/lessons/${lessonId}`);
  }

  public static async getAssignmentsByLesson(lessonId: string): Promise<any> {
    return this.getApi(`planning/classes/assignments?lessonId=${lessonId}`);
  }

  public static async getClassTotalHours(classId: string): Promise<any> {
    return this.getApi(`planning/classes/${classId}/lessons/total-hours`);
  }

  public static async getAllClassesTotalHours(): Promise<any> {
    return this.getApi('planning/classes/total-hours');
  }

  public static async getPlanningSettings(): Promise<any> {
    return this.getApi('planning/settings');
  }

  public static async updatePlanningSettings(data: {
    workingDays?: number;
    dailyMaxLessons?: number;
    lessonDuration?: number;
    breakBetweenLessons?: number;
    minBreakBetweenLessons?: number;
    maxConsecutiveLessons?: number;
    autoDistribute?: boolean;
    considerTeacherPreferences?: boolean;
    balanceWorkload?: boolean;
    preventConsecutiveSameLessons?: boolean;
    schoolStartTime?: string;
    lunchBreakStart?: string;
    lunchBreakDuration?: number;
    workingDaysList?: string[];
  }): Promise<any> {
    return this.putApi('planning/settings', data);
  }

  public static async getTeacherAvailability(teacherId: string): Promise<any> {
    return this.getApi(`planning/teachers/${teacherId}/availability`);
  }

  public static async setTeacherAvailability(teacherId: string, data: {
    slots?: Array<{ dayOfWeek: string; period: number; isAvailable?: boolean; preferenceLevel?: number }>;
    weekly?: Record<string, Array<{ period: number; isAvailable?: boolean; preferenceLevel?: number }>>;
  }): Promise<any> {
    return this.putApi(`planning/teachers/${teacherId}/availability`, data);
  }

  public static async getClassSchedule(classId: string): Promise<any> {
    return this.getApi(`planning/schedule/class/${classId}`);
  }

  public static async getTeacherScheduleEntries(teacherId: string): Promise<any> {
    return this.getApi(`planning/schedule/teacher/${teacherId}`);
  }

  public static async getAllScheduleEntries(): Promise<any> {
    return this.getApi('planning/schedule/all');
  }

  public static async getAllClassSchedulesAggregated(): Promise<any> {
    return this.getApi('planning/schedule/classes-aggregated');
  }

  public static async createScheduleEntry(data: {
    classId: string;
    teacherId: string;
    lessonId: string;
    dayOfWeek: string;
    period: number;
    isLocked?: boolean;
  }): Promise<any> {
    return this.postApi('planning/schedule/entries', data);
  }

  public static async deleteScheduleEntry(id: string): Promise<any> {
    return this.deleteApi(`planning/schedule/entries/${id}`);
  }

  public static async lockScheduleEntry(id: string, isLocked: boolean): Promise<any> {
    return this.putApi(`planning/schedule/entries/${id}/lock`, { isLocked });
  }

  public static async clearSchedule(classId?: string, onlyAutoGenerated?: boolean): Promise<any> {
    return this.postApi('planning/schedule/clear', { classId, onlyAutoGenerated });
  }

  public static async generateSchedule(options?: {
    clearExisting?: boolean;
    classIds?: string[];
  }): Promise<any> {
    return this.postApi('planning/schedule/generate', options || {});
  }

  public static async getScheduleSummary(): Promise<any> {
    return this.getApi('planning/schedule/summary');
  }

  public static async bulkDeleteTeacherAssignments(assignmentIds: string[]): Promise<any> {
    return this.postApi('planning/teacher-assignments/bulk-delete', { assignmentIds });
  }

  public static async bulkDeleteClassAssignments(classId: string): Promise<any> {
    return this.deleteApi(`planning/classes/${classId}/lessons/bulk-delete`);
  }

  public static async updateClassLessonAssignmentHours(classId: string, lessonId: string, hours: number): Promise<any> {
    return this.patchApi(`planning/classes/${classId}/lessons/${lessonId}/hours`, { hours });
  }

  public static async getMyTeacherSchedule(): Promise<any> {
    return this.getApi('planning/schedule/my-schedule');
  }

  public static async getMyClassSchedule(): Promise<any> {
    return this.getApi('planning/schedule/my-class-schedule');
  }

  public static async exportDepartmentSchedules(): Promise<Blob> {
    const endpoint = `/planning/schedule/export-departments`;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) throw new Error(`Excel dışa aktarma hatası! Durum: ${response.status}`);
    return response.blob();
  }

  public static async getScheduledTeachers(branch?: string): Promise<any[]> {
    const qs = new URLSearchParams();
    if (branch) qs.append("branch", branch);

    const query = qs.toString();
    const endpoint = `/planning/schedule/scheduled-teachers${query ? `?${query}` : ""}`;

    return this.request<any[]>(endpoint);
  }

  public static async exportTeacherSchedules(branch?: string): Promise<Blob> {
    const qs = new URLSearchParams();
    if (branch) qs.append("branch", branch);

    const query = qs.toString();
    const endpoint = `/planning/schedule/export-teachers${query ? `?${query}` : ""}`;

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) throw new Error(`Excel dışa aktarma hatası! Durum: ${response.status}`);
    return response.blob();
  }

  public static async getTodoCategories(params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/todos/categories${query ? `?${query}` : ""}`);
  }

  public static async createTodoCategory(data: { name: string; color: string }): Promise<any> {
    return this.postApi("todos/categories", data);
  }

  public static async updateTodoCategory(categoryId: string, data: { name?: string; color?: string }): Promise<any> {
    return this.putApi(`todos/categories/${categoryId}`, data);
  }

  public static async deleteTodoCategory(categoryId: string): Promise<void> {
    return this.deleteApi(`todos/categories/${categoryId}`);
  }

  public static async getTodoTags(params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/todos/tags${query ? `?${query}` : ""}`);
  }

  public static async createTodoTag(data: { name: string; color: string }): Promise<any> {
    return this.postApi("todos/tags", data);
  }

  public static async updateTodoTag(tagId: string, data: { name?: string; color?: string }): Promise<any> {
    return this.putApi(`todos/tags/${tagId}`, data);
  }

  public static async deleteTodoTag(tagId: string): Promise<void> {
    return this.deleteApi(`todos/tags/${tagId}`);
  }

  public static async getTodoItems(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    categoryId?: string;
    completed?: boolean;
    cancelled?: boolean;
  }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    if (params?.categoryId) qs.append("categoryId", params.categoryId);
    if (params?.completed !== undefined) qs.append("completed", String(params.completed));
    if (params?.cancelled !== undefined) qs.append("cancelled", String(params.cancelled));
    const query = qs.toString();
    return this.request<any>(`/todos/items${query ? `?${query}` : ""}`);
  }

  public static async getTodoItem(itemId: string): Promise<any> {
    return this.getApi(`todos/items/${itemId}`);
  }

  public static async createTodoItem(data: {
    text: string;
    todoCategoryId: string;
    priority?: string;
    estimatedTimeValue?: number;
    estimatedTimeUnit?: string;
    tagIds?: string[];
  }): Promise<any> {
    return this.postApi("todos/items", data);
  }

  public static async updateTodoItem(
    itemId: string,
    data: {
      text?: string;
      todoCategoryId?: string;
      priority?: string;
      estimatedTimeValue?: number;
      estimatedTimeUnit?: string;
      tagIds?: string[];
    }
  ): Promise<any> {
    return this.putApi(`todos/items/${itemId}`, data);
  }

  public static async deleteTodoItem(itemId: string): Promise<void> {
    return this.deleteApi(`todos/items/${itemId}`);
  }

  public static async toggleTodoComplete(itemId: string): Promise<any> {
    return this.postApi(`todos/items/${itemId}/toggle-complete`, {});
  }

  public static async toggleTodoCancel(itemId: string): Promise<any> {
    return this.postApi(`todos/items/${itemId}/toggle-cancel`, {});
  }

  public static async getDailyRoutines(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();

    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);

    const query = qs.toString();
    return this.request<any>(`/todos/daily-routines${query ? `?${query}` : ""}`);
  }

  public static async createDailyRoutine(data: {
    text: string;
    priority?: string;
    color?: string;
  }): Promise<any> {
    return this.postApi("todos/daily-routines", data);
  }

  public static async getDailyRoutine(routineId: string): Promise<any> {
    return this.getApi(`todos/daily-routines/${routineId}`);
  }

  public static async updateDailyRoutine(
    routineId: string,
    data: {
      text?: string;
      priority?: string;
      color?: string;
      order?: number;
      isActive?: boolean;
    }
  ): Promise<any> {
    return this.putApi(`todos/daily-routines/${routineId}`, data);
  }

  public static async deleteDailyRoutine(routineId: string): Promise<void> {
    return this.deleteApi(`todos/daily-routines/${routineId}`);
  }

  public static async toggleDailyRoutineComplete(routineId: string): Promise<any> {
    return this.postApi(`todos/daily-routines/${routineId}/toggle-complete`, {});
  }

  public static async getSmartRoutineSuggestions(): Promise<any> {
    return this.getApi("todos/smart-suggestions");
  }

  public static async generateSmartRoutineSuggestions(params?: {
    sourceYear?: number;
    sourceMonth?: number;
  }): Promise<any> {
    return this.postApi("todos/smart-suggestions", params || {});
  }

  public static async generateVideoWatchingSuggestions(params?: {
    sourceYear?: number;
    sourceMonth?: number;
  }): Promise<any> {
    return this.postApi("todos/smart-suggestions/video", params || {});
  }

  public static async getAvailableMonthsForSuggestions(): Promise<any> {
    return this.getApi("todos/smart-suggestions/available-months");
  }

  public static async updateSmartRoutineProgress(
    suggestionId: string,
    completedQuestions: number
  ): Promise<any> {
    return this.putApi(`todos/smart-suggestions/${suggestionId}/progress`, {
      completedQuestions,
    });
  }

  public static async getAnnouncements(filters?: { page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);

    const queryString = params.toString();
    const endpoint = `/announcements${queryString ? `?${queryString}` : ""}`;

    return this.request<any>(endpoint);
  }

  public static async getLatestAnnouncement(): Promise<any[]> {
    return this.getApi("announcements/latest");
  }

  public static async getAnnouncement(announcementId: string): Promise<any> {
    const response = await this.getAnnouncements({
      page: 1,
      limit: 100,
    });

    const announcements = response?.data || [];
    const announcement = announcements.find((a: any) => a.id === announcementId);

    if (!announcement) {
      throw new Error("Duyuru bulunamadı");
    }

    return announcement;
  }

  public static async createAnnouncement(data: {
    title: string;
    message: string;
    display?: boolean;
    departmentIds?: string[];
  }): Promise<any> {
    return this.postApi("announcements", data);
  }

  public static async updateAnnouncement(
    announcementId: string,
    data: {
      title?: string;
      message?: string;
      display?: boolean;
      departmentIds?: string[];
    }
  ): Promise<any> {
    return this.putApi(`announcements/${announcementId}`, data);
  }

  public static async deleteAnnouncement(announcementId: string): Promise<void> {
    return this.deleteApi(`announcements/${announcementId}`);
  }

  public static async getTasks(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
    type?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params?.search) queryParams.append("search", params.search);
    if (params?.type) queryParams.append("type", params.type);

    const query = queryParams.toString();
    return this.getApi(`tasks${query ? `?${query}` : ""}`);
  }

  public static async getTaskById(taskId: string): Promise<any> {
    return this.getApi(`tasks/${taskId}`);
  }

  public static async createTask(data: {
    title: string;
    description: string;
    frequency: string;
    format: string;
    startDate: string;
    endDate: string;
    departmentIds?: string[];
    userIds?: string[];
    taskType?: string;
  }): Promise<any> {
    return this.postApi("tasks", data);
  }

  public static async updateTask(
    taskId: string,
    data: {
      title?: string;
      description?: string;
      frequency?: string;
      format?: string;
      startDate?: string;
      endDate?: string;
      departmentIds?: string[];
      taskType?: string;
    }
  ): Promise<any> {
    return this.putApi(`tasks/${taskId}`, data);
  }

  public static async deleteTask(taskId: string): Promise<void> {
    return this.deleteApi(`tasks/${taskId}`);
  }

  public static async submitTask(
    taskId: string,
    data: {
      content?: string;
      attachments?: any[];
    }
  ): Promise<any> {
    return this.postApi(`tasks/${taskId}/submit`, data);
  }

  public static async getMyTaskSubmission(taskId: string, periodDate?: string): Promise<any> {
    const query = periodDate ? `?periodDate=${periodDate}` : "";
    return this.getApi(`tasks/${taskId}/my-submission${query}`);
  }

  public static async getTaskSubmissions(taskId: string, periodDate?: string): Promise<any> {
    const query = periodDate ? `?periodDate=${periodDate}` : "";
    return this.getApi(`tasks/${taskId}/submissions${query}`);
  }

  public static async reviewTaskSubmission(submissionId: string, action: "APPROVE" | "REJECT", reviewNote?: string): Promise<any> {
    return this.patchApi(`tasks/submissions/${submissionId}/review`, {
      action,
      reviewNote,
    });
  }

  public static async getMySubmissions(params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const query = queryParams.toString();
    return this.getApi(`tasks/submissions/my${query ? `?${query}` : ""}`);
  }

  public static async getTaskSuggestions(limit?: number): Promise<any> {
    const query = limit ? `?limit=${limit}` : "";

    return this.getApi(`tasks/suggestions${query}`);
  }

  public static async getStudentReportSummary(
    userId: string,
    filters?: {
      lessonId?: string;
      topicId?: string;
      type?: "all" | "homework" | "independent";
    }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);
    if (filters?.type && filters.type !== "all") params.append("type", filters.type);

    const query = params.toString();
    return this.request<any>(`${studentReportBase(userId)}/summary${query ? `?${query}` : ""}`);
  }

  public static async getStudentTestsList(
    userId: string,
    filters?: {
      lessonId?: string;
      topicId?: string;
      type?: "all" | "homework" | "independent";
      sortBy?: string;
      sortOrder?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);
    if (filters?.type && filters.type !== "all") params.append("type", filters.type);
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const query = params.toString();
    return this.request<any>(`${studentReportBase(userId)}/tests${query ? `?${query}` : ""}`);
  }

  public static async getStudentReport(
    userId: string,
    filters?: {
      lessonId?: string;
      topicId?: string;
      type?: "all" | "homework" | "independent";
      sortBy?: string;
      sortOrder?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);
    if (filters?.type && filters.type !== "all") params.append("type", filters.type);
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const query = params.toString();
    return this.request<any>(`/report/student/${userId}${query ? `?${query}` : ""}`);
  }

  public static async getStudentAvailableLessons(userId: string): Promise<any> {
    return this.request<any>(`/report/student/${userId}/lessons`);
  }

  public static async getStudentAvailableTopics(userId: string, lessonId?: string): Promise<any> {
    const params = new URLSearchParams();
    if (lessonId) params.append("lessonId", lessonId);

    const query = params.toString();
    return this.request<any>(`/report/student/${userId}/topics${query ? `?${query}` : ""}`);
  }

  // Raporlarım: öğrencinin kendi Koçum planı (salt-okur; /navigator/coach/plan gibi plan uzlaştırması çalıştırmaz)
  public static async getMyCoachPlan(range?: { from: string; to: string }): Promise<ObservePlanView> {
    const qs = range ? `?from=${range.from}&to=${range.to}` : "";
    return this.request<ObservePlanView>(`/report/me/coach-plan${qs}`);
  }

  // Raporlarım: ders başına açılan videolar
  public static async getMyVideoProgress(): Promise<import("../types/student-report").MyVideoProgress> {
    return this.request(`/report/me/videos`);
  }

  public static async getStudentPracticeExams(userId: string): Promise<StudentPracticeExamHistory> {
    return this.request<StudentPracticeExamHistory>(`${studentReportBase(userId)}/practice-exams`);
  }

  public static async getStudentPracticeExamResult(userId: string, examId: string): Promise<any> {
    return this.request<any>(`${studentReportBase(userId)}/practice-exams/${examId}`);
  }

  // Öğretmen/veli rapor ekranı: öğrencinin deneme hata kitapçığı
  public static async downloadStudentPracticeExamErrorBooklet(userId: string, examId: string): Promise<{ blob: Blob; fileName?: string }> {
    if (userId === MY_REPORT) return this.downloadMyPracticeExamErrorBooklet(examId);
    return this.requestBlob(`/report/student/${userId}/practice-exams/${examId}/error-booklet.pdf`);
  }

  // Öğretmen/veli rapor ekranı: öğrencinin detaylı karne PDF'i
  public static async downloadStudentPracticeExamReportCard(userId: string, examId: string): Promise<{ blob: Blob; fileName?: string }> {
    if (userId === MY_REPORT) return this.downloadMyPracticeExamReportCard(examId);
    return this.requestBlob(`/report/student/${userId}/practice-exams/${examId}/report-card.pdf`);
  }

  public static async getParentChildrenReports(filters?: {
    type?: "all" | "homework" | "independent";
    lessonId?: string;
    topicId?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.type && filters.type !== "all") params.append("type", filters.type);
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);

    const query = params.toString();
    return this.request<any>(`/report/parent/children-reports${query ? `?${query}` : ""}`);
  }

  public static async getDepartmentWeeks(departmentId: string): Promise<any> {
    return this.getApi(`report/department/${departmentId}/weeks`);
  }

  public static async getDepartmentHomeworkReport(
    departmentId: string,
    data: {
      weekNumbers: number[];
      page: number;
      limit: number;
      countMode?: 'individual' | 'activity';
    }
  ): Promise<any> {
    return this.postApi(`report/department/${departmentId}/homework-report`, data);
  }

  public static async exportDepartmentHomeworkReport(
    departmentId: string,
    weekNumbers: number[],
    countMode?: 'individual' | 'activity'
  ): Promise<Blob> {
    const endpoint = `/report/department/${departmentId}/homework-report/export`;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ weekNumbers, countMode }),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  public static async getSuccessScoreReport(
    departmentId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    data: Array<{
      user: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string;
        schoolNumber?: string;
        department?: any;
        avatar?: string | null;
      };
      successRate: number;
      totalHomeworks: number;
      completedHomeworks: number;
      completionRate: number;
      lessonScores: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
      }>;
    }>;
    meta: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
      perPage: number;
    };
    departmentScore: number;
    lessonScores: Array<{
      id: string;
      title: string;
      successRate: number;
      totalHomeworks: number;
      completedHomeworks: number;
      completionRate: number;
    }>;
    statistics: {
      totalStudents: number;
      totalHomeworks: number;
      completedHomeworks: number;
      overallCompletionRate: number;
    };
  }> {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(limit));
    const query = params.toString();
    return this.request<any>(`/report/success-score/${departmentId}${query ? `?${query}` : ""}`);
  }

  public static async getUserDetailScores(userId: string): Promise<{
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email?: string;
      schoolNumber?: string;
      department?: any;
      avatar?: string | null;
    };
    successRate: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
    lessonScores: Array<{
      id: string;
      title: string;
      successRate: number;
      totalHomeworks: number;
      completedHomeworks: number;
      completionRate: number;
      topics: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
      }>;
    }>;
  }> {
    return this.request<any>(`/report/success-score-user/${userId}`);
  }

  public static async getBranchSuccessScoreReport(
    parentDepartmentId: string
  ): Promise<{
    parentDepartment: {
      id: string;
      name: string;
      color?: string;
    } | null;
    branches: Array<{
      department: {
        id: string;
        name: string;
        color?: string;
      };
      successRate: number;
      totalStudents: number;
      totalHomeworks: number;
      completedHomeworks: number;
      completionRate: number;
      lessonScores: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
      }>;
    }>;
    statistics: {
      totalBranches: number;
      totalStudents: number;
      totalHomeworks: number;
      completedHomeworks: number;
      overallCompletionRate: number;
      overallSuccessRate: number;
    };
  }> {
    return this.request<any>(`/report/branch-success-score/${parentDepartmentId}`);
  }

  public static async getMonthlyReports(
    userId: string,
    options?: GetMonthlyReportsOptions
  ): Promise<MonthlyReportsResponse> {
    const params = new URLSearchParams();
    if (options?.startPeriod) params.append("startPeriod", options.startPeriod);
    if (options?.endPeriod) params.append("endPeriod", options.endPeriod);
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));

    const query = params.toString();
    return this.request<MonthlyReportsResponse>(
      `/report/monthly/${userId}${query ? `?${query}` : ""}`
    );
  }

  public static async getMonthlyReport(
    userId: string,
    period: string
  ): Promise<MonthlySuccessReport> {
    return this.request<MonthlySuccessReport>(`/report/monthly/${userId}/${period}`);
  }

  public static async getCurrentMonthLiveReport(
    userId: string
  ): Promise<MonthlySuccessReport> {
    return this.request<MonthlySuccessReport>(`/report/monthly/${userId}/current`);
  }

  public static async getAvailablePeriods(
    userId: string
  ): Promise<AvailablePeriod[]> {
    return this.request<AvailablePeriod[]>(`/report/monthly/${userId}/available-periods`);
  }

  public static async compareMonthlyReports(
    userId: string,
    period1: string,
    period2: string
  ): Promise<MonthlyReportComparison> {
    const params = new URLSearchParams();
    params.append("period1", period1);
    params.append("period2", period2);

    return this.request<MonthlyReportComparison>(
      `/report/monthly/${userId}/compare?${params.toString()}`
    );
  }

  public static async getDepartmentMonthlyReport(
    departmentId: string,
    year: number,
    month: number,
    options?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }
  ): Promise<{
    data: MonthlySuccessReport[];
    meta: { totalItems: number; totalPages: number; currentPage: number; perPage: number };
    departmentScore: number;
    lessonScores: Array<{ id: string; name: string; score: number; totalHomeworks: number; completedHomeworks: number }>;
    statistics: { totalStudents: number; totalHomeworks: number; completedHomeworks: number; overallCompletionRate: number };
    reportPeriod: string;
  }> {
    const params = new URLSearchParams();
    params.append('year', String(year));
    params.append('month', String(month));
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

    return this.request(`/report/monthly/department/${departmentId}?${params.toString()}`);
  }

  public static async getAvailableDepartmentPeriods(
    departmentId: string
  ): Promise<Array<{ period: string; year: number; month: number; name: string }>> {
    return this.request(`/report/monthly/department/${departmentId}/available-periods`);
  }

  public static async getDepartmentMultiMonthReport(
    departmentId: string,
    periods: string[],
    options?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }
  ): Promise<{
    data: Array<{
      userId: string;
      user: { id: string; firstName: string; lastName: string; email?: string };
      periodData: Record<string, {
        successRate: number;
        completionRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        totalCorrect: number;
        totalWrong: number;
        totalEmpty: number;
        totalQuestions: number;
        lessonDetails: any[];
      }>;
      avgSuccessRate: number;
      avgCompletionRate: number;
      periodCount: number;
    }>;
    meta: { totalItems: number; totalPages: number; currentPage: number; perPage: number };
    periods: Array<{ period: string; year: number; month: number; name: string }>;
    periodData: Record<string, {
      avgSuccessRate: number;
      totalHomeworks: number;
      completedHomeworks: number;
      completionRate: number;
      studentCount: number;
    }>;
    lessonScores: Array<{ id: string; name: string; score: number; totalHomeworks: number; completedHomeworks: number }>;
    statistics: { totalStudents: number; totalHomeworks: number; completedHomeworks: number; overallCompletionRate: number };
  }> {
    return this.request(`/report/monthly/department/${departmentId}/multi`, {
      method: 'POST',
      body: JSON.stringify({
        periods,
        page: options?.page,
        limit: options?.limit,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder
      })
    });
  }

  public static async getPracticeExams(params?: {
    page?: number;
    limit?: number;
    search?: string;
    systemType?: "publisher" | "internal";
    levelType?: "lgs" | "yks";
    audience?: "internal" | "external";
    gradeLevel?: number;
    status?: "with-results" | "without-results";
  }): Promise<{ data: PracticeExam[]; meta?: any }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.systemType) qs.append("systemType", params.systemType);
    if (params?.levelType) qs.append("levelType", params.levelType);
    if (params?.audience) qs.append("audience", params.audience);
    if (params?.gradeLevel) qs.append("gradeLevel", String(params.gradeLevel));
    if (params?.status) qs.append("status", params.status);
    const query = qs.toString();
    const path = `practice-exams${query ? `?${query}` : ""}`;
    return this.getApi(path);
  }

  public static async getPracticeExam(examId: string): Promise<PracticeExam> {
    return this.getApi(`practice-exams/${examId}`);
  }

  // Seviye tespiti / şube önerisi (Raporlar): seçilen şubeler + denemeler → alan netine göre seviye şubesi önerisi (salt okur)
  public static async runSeviyeTespit(body: SeviyeTespitIstek): Promise<SeviyeTespitSonuc> {
    return this.request<SeviyeTespitSonuc>("/practice-exams/seviye-tespit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  public static async downloadSeviyeTespitExcel(body: SeviyeTespitIstek): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob("/practice-exams/seviye-tespit?format=xlsx", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  public static async createPracticeExam(payload: {
    name: string;
    date?: string;
    systemType?: "publisher" | "internal";
    levelType?: "lgs" | "yks";
    yksMode?: "full" | "single_tyt" | "single_ayt";
    audience?: "internal" | "external" | "both";
    gradeLevel?: number | null;
    departmentIds?: string[];
    alanPuani?: boolean;
    answerKey?: File;
    answerKeyTYT?: File;
    answerKeyAYT?: File;
    formDefinition?: File;
  }): Promise<PracticeExam> {
    const formData = new FormData();
    formData.append('name', payload.name);

    if (payload.date) formData.append('date', payload.date);
    if (payload.systemType) formData.append('systemType', payload.systemType);
    if (payload.levelType) formData.append('levelType', payload.levelType);
    if (payload.yksMode) formData.append('yksMode', payload.yksMode);
    if (payload.audience) formData.append('audience', payload.audience);
    if (payload.gradeLevel) formData.append('gradeLevel', String(payload.gradeLevel));
    if (payload.departmentIds?.length) formData.append('departmentIds', JSON.stringify(payload.departmentIds));
    if (payload.alanPuani) formData.append('alanPuani', 'true');
    if (payload.answerKey) formData.append('answerKey', payload.answerKey);
    if (payload.answerKeyTYT) formData.append('answerKeyTYT', payload.answerKeyTYT);
    if (payload.answerKeyAYT) formData.append('answerKeyAYT', payload.answerKeyAYT);
    if (payload.formDefinition) formData.append('formDefinition', payload.formDefinition);

    return this.request<PracticeExam>('/practice-exams', {
      method: 'POST',
      body: formData,
    });
  }

  // formDefinition (isteğe bağlı .fmt/.ftm): verilirse sonuç dosyası alan alan form tanımıyla okunur
  public static async uploadPracticeExamResults(examId: string, resultFile: File, formDefinition?: File | null): Promise<any> {
    const formData = new FormData();
    formData.append("resultFile", resultFile);
    if (formDefinition) formData.append("formDefinition", formDefinition);
    return this.request<any>(`/practice-exams/${examId}/results`, {
      method: "POST",
      body: formData,
    });
  }

  public static async getPracticeExamResults(
    examId: string,
    params?: { page?: number; limit?: number; sortBy?: string; schoolId?: string; search?: string; missingIdentity?: boolean }
  ): Promise<PracticeExamResultSummary | any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.schoolId) qs.append("schoolId", params.schoolId);
    if (params?.search) qs.append("search", params.search);
    if (params?.missingIdentity) qs.append("missingIdentity", "true");

    const query = qs.toString();
    return this.getApi(`practice-exams/${examId}/results${query ? `?${query}` : ""}`);
  }

  public static async bulkDeletePracticeExamResults(examId: string, resultIds: string[]): Promise<any> {
    return this.request<any>(`/practice-exams/${examId}/results/bulk-delete`, {
      method: "POST",
      body: JSON.stringify({ resultIds }),
    });
  }

  public static async bulkRegeneratePracticeExamResults(examId: string, resultIds: string[]): Promise<any> {
    return this.request<any>(`/practice-exams/${examId}/results/bulk-regenerate`, {
      method: "POST",
      body: JSON.stringify({ resultIds }),
    });
  }

  public static async deletePracticeExam(examId: string): Promise<void> {
    return this.deleteApi(`practice-exams/${examId}`);
  }

  public static async submitInternalPracticeExam(
    examId: string,
    payload: {
      kitapcikTuru?: 'A' | 'B' | null;
      answers: Record<string, Array<string | null>>;
      branchSelections?: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>;
    }
  ): Promise<any> {
    return this.request<any>(`/practice-exams/${examId}/submit`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  public static async saveInternalPracticeExam(
    examId: string,
    payload: {
      kitapcikTuru?: 'A' | 'B' | null;
      answers: Record<string, Array<string | null>>;
      branchSelections?: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>;
    }
  ): Promise<any> {
    return this.request<any>(`/practice-exams/${examId}/save`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  public static async getMyPracticeExamResult(examId: string): Promise<any> {
    return this.getApi(`practice-exams/${examId}/my-result`);
  }

  public static async getMyPracticeExamSolutions(examId: string): Promise<any> {
    return this.getApi(`practice-exams/${examId}/my-solutions`);
  }

  public static async getExamAnalysis(payload: {
    examId?: string;
    // Analiz bu sonuca kaydedilir (öğretmen/yönetici başkasının karnesinde de)
    resultId?: string;
    examName: string;
    subjects: Array<{
      name: string;
      correct: number;
      incorrect: number;
      empty: number;
      net: number;
      totalQ: number;
      weakTopics: Array<{ name: string; incorrect: number; questionNumber?: number }>;
    }>;
    totalNet: number;
    totalQuestions: number;
    scorePercentage: number;
  }): Promise<any> {
    return this.request<any>('/ai/exam-analysis', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public static async getMyPracticeExamProgress(examId: string): Promise<any> {
    return this.getApi(`practice-exams/${examId}/my-progress`);
  }

  public static async getPracticeExamAnswerKey(examId: string, segment?: string): Promise<{
    exam: PracticeExam;
    activeSegment?: string | null;
    sheet?: {
      publisherName?: string;
      testName?: string;
      testCode?: string;
      columns: string[];
      rows: string[][];
    } | null;
    sheets?: Array<{
      key: string;
      label: string;
      sheet: {
        publisherName?: string;
        testName?: string;
        testCode?: string;
        columns: string[];
        rows: string[][];
      } | null;
    }>;
    rows: Array<{
      testId: number;
      testName: string;
      subjectName: string;
      aQuestionNumber: number;
      bQuestionNumber: number;
      correctAnswer: string;
      absoluteQuestionNumber: number;
      achievement: string;
    }>;
  }> {
    const qs = new URLSearchParams();
    if (segment) qs.append("segment", segment);
    const query = qs.toString();
    return this.getApi(`practice-exams/${examId}/answer-key${query ? `?${query}` : ""}`);
  }

  public static async exportPracticeExamAnswerKey(examId: string, segment?: string): Promise<{ blob: Blob; fileName?: string }> {
    const qs = new URLSearchParams();
    if (segment) qs.append("segment", segment);
    const query = qs.toString();
    return this.requestBlob(`/practice-exams/${examId}/answer-key/export${query ? `?${query}` : ""}`);
  }

  // Detaylı karne analizi PDF'i: seçili sonuçlar (ya da şube) tek PDF'te
  // Toplu karnenin başarı sırası (29 Eki): çok karne parça parça indirilirken parçalar bu sırayla bölünür
  public static async orderPracticeExamReportCards(examId: string, resultIds: string[]): Promise<{ resultIds: string[] }> {
    return this.request<{ resultIds: string[] }>(`/practice-exams/${examId}/report-cards/order`, {
      method: "POST",
      body: JSON.stringify({ resultIds }),
    });
  }

  public static async downloadPracticeExamReportCards(examId: string, resultIds: string[], departmentId?: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/practice-exams/${examId}/report-cards.pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resultIds, ...(departmentId ? { departmentId } : {}) }),
    });
  }

  // Optik okuyucu sonuçları (form tanımı + TXT) → Akademi sistemi YKS denemesi; dryRun önizler, false yazar
  public static async uploadPracticeExamOpticalResults(
    examId: string,
    files: { tytForm?: File | null; tytResult?: File | null; aytForm?: File | null; aytResult?: File | null },
    options: { dryRun: boolean; defaultBranch?: "din" | "felsefe"; fieldMap?: Partial<Record<"tyt" | "ayt", string[]>>; matchDepartmentId?: string; schoolId?: string }
  ): Promise<any> {
    const formData = new FormData();
    (Object.entries(files) as Array<[string, File | null | undefined]>).forEach(([field, file]) => {
      if (file) formData.append(field, file);
    });
    formData.append("dryRun", String(options.dryRun));
    if (options.defaultBranch) formData.append("defaultBranch", options.defaultBranch);
    if (options.fieldMap && Object.keys(options.fieldMap).length) formData.append("fieldMap", JSON.stringify(options.fieldMap));
    if (options.matchDepartmentId) formData.append("matchDepartmentId", options.matchDepartmentId);
    if (options.schoolId) formData.append("schoolId", options.schoolId);
    return this.request<any>(`/practice-exams/${examId}/optical-results`, {
      method: "POST",
      body: formData,
    });
  }

  // LGS optik (kurum içi / kurum dışı). Kurum dışında schoolId yoksa satırlar optikteki kurum koduna göre okullara dağıtılır.
  public static async uploadLgsOpticalResults(
    examId: string,
    files: { form?: File | null; result?: File | null; form2?: File | null; result2?: File | null },
    options: { dryRun: boolean; mode: "internal" | "external"; schoolId?: string | null; ignoreSchoolCode?: boolean }
  ): Promise<LgsOpticalImportSummary> {
    const formData = new FormData();
    (Object.entries(files) as Array<[string, File | null | undefined]>).forEach(([field, file]) => {
      if (file) formData.append(field, file);
    });
    formData.append("dryRun", String(options.dryRun));
    formData.append("mode", options.mode);
    if (options.schoolId) formData.append("schoolId", options.schoolId);
    if (options.schoolId && options.ignoreSchoolCode) formData.append("ignoreSchoolCode", "true");
    return this.request<LgsOpticalImportSummary>(`/practice-exams/${examId}/optical-results-lgs`, {
      method: "POST",
      body: formData,
    });
  }

  // Hesabı olmayan sonuç satırının kimliği (optikte eksik/yanlış kodlanan TC, okul no, ad, sınıf)
  public static async updatePracticeExamResultIdentity(
    examId: string,
    resultId: string,
    data: { tcNumber?: string | null; studentNumber?: string | null; fullName?: string | null; className?: string | null }
  ): Promise<any> {
    return this.request<any>(`/practice-exams/${examId}/results/${resultId}/identity`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // ===== Dış kurumlar (okullar) =====
  public static async getExternalSchools(): Promise<ExternalSchool[]> {
    return this.request<ExternalSchool[]>("/external-schools");
  }

  public static async getExternalSchoolOptions(): Promise<ExternalSchoolOption[]> {
    return this.request<ExternalSchoolOption[]>("/external-schools/options");
  }

  public static async getExternalPartnerOverview(): Promise<PartnerOverview> {
    return this.request<PartnerOverview>("/external-schools/partner/overview");
  }

  public static async getExternalSchoolSummary(schoolId: string): Promise<ExternalSchoolSummary> {
    return this.request<ExternalSchoolSummary>(`/external-schools/${schoolId}/summary`);
  }

  public static async createExternalSchool(data: ExternalSchoolInput): Promise<ExternalSchool> {
    return this.request<ExternalSchool>("/external-schools", { method: "POST", body: JSON.stringify(data) });
  }

  public static async updateExternalSchool(schoolId: string, data: ExternalSchoolInput): Promise<ExternalSchool> {
    return this.request<ExternalSchool>(`/external-schools/${schoolId}`, { method: "PATCH", body: JSON.stringify(data) });
  }

  public static async deleteExternalSchool(schoolId: string): Promise<void> {
    await this.request<void>(`/external-schools/${schoolId}`, { method: "DELETE" });
  }

  public static async rotateExternalSchoolLink(schoolId: string): Promise<ExternalSchool> {
    return this.request<ExternalSchool>(`/external-schools/${schoolId}/rotate-link`, { method: "POST" });
  }

  public static async setExternalSchoolAdminPassword(schoolId: string): Promise<{ school: ExternalSchool; password: string }> {
    return this.request<{ school: ExternalSchool; password: string }>(`/external-schools/${schoolId}/admin-password`, { method: "POST" });
  }

  // ===== Herkese açık karne (oturum token'ı EKLENMEZ; karne/okul token'ı parametreyle gelir) =====
  private static async karneRequest<T>(endpoint: string, options: RequestInit = {}, token?: string | null): Promise<T> {
    const headers: Record<string, string> = { "Content-Type": "application/json", ...((options.headers as Record<string, string>) || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/karne${endpoint}`, { ...options, headers });
    let data: any = null;
    try { data = await response.json(); } catch (_) { }
    if (!response.ok) {
      const error: any = new Error(data?.message || `HTTP Hatası! Durum: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return data as T;
  }

  private static async karneBlob(endpoint: string, token: string, options: RequestInit = {}): Promise<{ blob: Blob; fileName?: string }> {
    const response = await fetch(`${API_BASE_URL}/karne${endpoint}`, {
      ...options,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...((options.headers as Record<string, string>) || {}) },
    });
    if (!response.ok) {
      let message = `HTTP Hatası! Durum: ${response.status}`;
      try { const data = await response.json(); message = data?.message || message; } catch (_) { }
      throw new Error(message);
    }
    const match = (response.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/i);
    return { blob: await response.blob(), fileName: match?.[1] };
  }

  public static async getKarneSchool(slug: string): Promise<PublicSchoolInfo> {
    return this.karneRequest<PublicSchoolInfo>(`/${encodeURIComponent(slug)}`);
  }

  public static async karneLookup(slug: string, data: { tc: string; studentNumber: string; turnstileToken: string }): Promise<KarneLookupResponse> {
    return this.karneRequest<KarneLookupResponse>(`/${encodeURIComponent(slug)}/lookup`, { method: "POST", body: JSON.stringify(data) });
  }

  public static async getKarneResult(slug: string, resultId: string, token: string): Promise<any> {
    return this.karneRequest<any>(`/${encodeURIComponent(slug)}/results/${resultId}`, {}, token);
  }

  public static async downloadKarnePdf(slug: string, resultId: string, token: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.karneBlob(`/${encodeURIComponent(slug)}/results/${resultId}/report-card.pdf`, token);
  }

  public static async karneAdminLogin(slug: string, data: { password: string; turnstileToken: string }): Promise<{ token: string; school: PublicSchoolInfo }> {
    return this.karneRequest<{ token: string; school: PublicSchoolInfo }>(`/${encodeURIComponent(slug)}/admin/login`, { method: "POST", body: JSON.stringify(data) });
  }

  public static async getKarneAdminExams(slug: string, token: string): Promise<SchoolAdminExam[]> {
    return this.karneRequest<SchoolAdminExam[]>(`/${encodeURIComponent(slug)}/admin/exams`, {}, token);
  }

  public static async getKarneAdminResults(slug: string, examId: string, token: string): Promise<{ exam: { id: string; name: string; date: string; testNames: Record<string, string>; gradeLevel: number | null }; rows: ExternalResultSummary[] }> {
    return this.karneRequest(`/${encodeURIComponent(slug)}/admin/exams/${examId}/results`, {}, token);
  }

  public static async getKarneAdminResult(slug: string, resultId: string, token: string): Promise<any> {
    return this.karneRequest<any>(`/${encodeURIComponent(slug)}/admin/results/${resultId}`, {}, token);
  }

  public static async downloadKarneAdminReportCards(slug: string, examId: string, token: string, resultIds?: string[]): Promise<{ blob: Blob; fileName?: string }> {
    return this.karneBlob(`/${encodeURIComponent(slug)}/admin/exams/${examId}/report-cards.pdf`, token, {
      method: "POST",
      body: JSON.stringify(resultIds?.length ? { resultIds } : {}),
    });
  }

  // Deneme hata kitapçığı PDF'i (kazanım bazlı): seçili sonuçlar tek PDF'te
  public static async downloadPracticeExamErrorBooklets(examId: string, resultIds: string[]): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/practice-exams/${examId}/error-booklets.pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resultIds }),
    });
  }

  // Öğrencinin kendi deneme hata kitapçığı
  public static async downloadMyPracticeExamErrorBooklet(examId: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/practice-exams/${examId}/my-result/error-booklet.pdf`);
  }

  // Öğrencinin kendi detaylı karne PDF'i
  public static async downloadMyPracticeExamReportCard(examId: string): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/practice-exams/${examId}/my-result/report-card.pdf`);
  }

  // Denemeyi görecek bölümler (boş dizi = herkes)
  public static async updatePracticeExamAudience(examId: string, departmentIds: string[], alanPuani?: boolean, extra: { name?: string; date?: string } = {}): Promise<PracticeExam> {
    return this.request<PracticeExam>(`/practice-exams/${examId}/audience`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ departmentIds, ...(alanPuani === undefined ? {} : { alanPuani }), ...extra }),
    });
  }

  public static async updatePracticeExamAnswerKey(examId: string, answerKey: File, segment?: string): Promise<any> {
    const formData = new FormData();
    formData.append("answerKey", answerKey);
    const qs = new URLSearchParams();
    if (segment) qs.append("segment", segment);
    const query = qs.toString();
    return this.request<any>(`/practice-exams/${examId}/answer-key${query ? `?${query}` : ""}`, {
      method: "PUT",
      body: formData,
    });
  }

  public static async updatePracticeExamAnswerKeySheet(examId: string, sheet: {
    publisherName?: string;
    testName?: string;
    testCode?: string;
    columns: string[];
    rows: string[][];
  }, segment?: string): Promise<any> {
    const qs = new URLSearchParams();
    if (segment) qs.append("segment", segment);
    const query = qs.toString();
    return this.request<any>(`/practice-exams/${examId}/answer-key/sheet${query ? `?${query}` : ""}`, {
      method: "PUT",
      body: JSON.stringify(sheet),
    });
  }

  static async getSettings(): Promise<any> {
    return this.getApi("settings");
  }

  static async updateSettings(data: FormData): Promise<any> {
    return this.request<any>("/settings", {
      method: "POST",
      body: data,
    });
  }

  static async uploadFavicons(data: FormData): Promise<any> {
    return this.request<any>("/settings/favicon", {
      method: "POST",
      body: data,
    });
  }

  static async resetFavicons(): Promise<any> {
    return this.deleteApi("settings/favicon");
  }

  static async resetCompanyLogo(): Promise<any> {
    return this.deleteApi("settings/logo");
  }

  public static async uploadFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<FileUploadResponse>("/files", {
      method: "POST",
      body: formData,
    });
  }

  public static async uploadSpeedReadingFreeReadingFile(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<any>("/speed-reading/content/upload", {
      method: "POST",
      body: formData,
    });
  }

  public static async getSpeedReadingStoredPdfContent(fileId: string): Promise<any> {
    return this.request<any>(`/speed-reading/content/files/${fileId}`, {
      method: "GET",
    });
  }

  public static async uploadNotebook(files: FileList, notebookName: string): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append("notebookName", notebookName);

    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    return this.request<FileUploadResponse>("/files/notebook", {
      method: "POST",
      body: formData,
    });
  }

  public static async uploadNotebookFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('notebookName', file.name);

    return this.request<FileUploadResponse>('/files/notebook', {
      method: 'POST',
      body: formData,
    });
  }

  public static async listFiles(page: number = 1, limit: number = 20): Promise<FileListResponse> {
    return this.request<FileListResponse>(`/files?page=${page}&limit=${limit}`, {
      method: "GET",
    });
  }

  public static async getFile(fileId: string): Promise<FileItem> {
    return this.request<FileItem>(`/files/${fileId}`, {
      method: "GET",
    });
  }

  public static async deleteFile(fileId: string): Promise<void> {
    return this.request<void>(`/files/${fileId}`, {
      method: "DELETE",
    });
  }

  public static async processFile(fileId: string): Promise<ProcessFileResponse[]> {
    return this.request<ProcessFileResponse[]>(`/files/${fileId}/process`, {
      method: "POST",
    });
  }

  public static async updateFileContent(
    fileId: string,
    pages: Array<{ pageNumber: number; type: string; content: QuestionContent }>
  ): Promise<UpdateContentResponse[]> {
    return this.request<UpdateContentResponse[]>(`/files/${fileId}/content`, {
      method: "PATCH",
      body: JSON.stringify({ pages }),
    });
  }

  public static async createCategory(data: {
    name: string;
    color?: string;
    icon?: string;
  }): Promise<FileFolder> {
    return this.postApi("files/categories", data);
  }

  public static async listCategories(): Promise<{
    categories: FileFolder[];
    rootFolders: FileFolder[];
    unassignedFiles: FileItem[];
  }> {
    return this.request<any>(`/files/categories`);
  }

  public static async getCategory(categoryId: string): Promise<FileFolder> {
    return this.request<FileFolder>(`/files/categories/${categoryId}`);
  }

  public static async updateCategory(
    categoryId: string,
    data: {
      name?: string;
      color?: string;
      icon?: string;
      order?: number;
    }
  ): Promise<FileFolder> {
    return this.patchApi(`files/categories/${categoryId}`, data);
  }

  public static async deleteCategory(categoryId: string): Promise<void> {
    return this.deleteApi(`files/categories/${categoryId}`);
  }

  public static async createFolder(data: {
    name: string;
    color?: string;
    icon?: string;
    categoryId?: string;
    parentId?: string | null;
  }): Promise<FileFolder> {
    return this.postApi("files/folders", data);
  }

  public static async listFolders(params?: {
    parentId?: string | null;
    includeFiles?: boolean;
  }): Promise<FolderListResponse> {
    const qs = new URLSearchParams();
    if (params?.parentId) qs.append("parentId", params.parentId);
    if (params?.includeFiles) qs.append("includeFiles", String(params.includeFiles));

    const query = qs.toString();
    return this.request<FolderListResponse>(`/files/folders${query ? `?${query}` : ""}`);
  }

  public static async getFolder(folderId: string): Promise<FileFolder> {
    return this.request<FileFolder>(`/files/folders/${folderId}`);
  }

  public static async updateFolder(
    folderId: string,
    data: {
      name?: string;
      color?: string;
      icon?: string;
      order?: number;
    }
  ): Promise<FileFolder> {
    return this.patchApi(`files/folders/${folderId}`, data);
  }

  public static async deleteFolder(folderId: string): Promise<void> {
    return this.deleteApi(`files/folders/${folderId}`);
  }

  public static async moveFileToFolder(fileId: string, folderId: string | null): Promise<FileItem> {
    return this.patchApi(`files/${fileId}/move`, { folderId });
  }

  public static async getFolderFiles(
    folderId: string,
    params?: { page?: number; limit?: number }
  ): Promise<FileListResponse> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));

    const query = qs.toString();
    return this.request<FileListResponse>(`/files/folders/${folderId}/files${query ? `?${query}` : ""}`);
  }

  // Öğrenci Navigatörü — konu/ünite düzeyi durum haritası (neredesin).
  public static async getNavigatorStatus(): Promise<NavigatorStatusResponse> {
    return this.request<NavigatorStatusResponse>(`/navigator/status`);
  }

  // NOT: startDiagnostic / startPractice / submitDiagnostic kaldırıldı — backend
  // uçları silindi (koç artık mevcut E-Eğitim test moduna yönlendiriyor).

  // Ders anlat atomu — video + serbest-konu AI context + test kapsamı.
  public static async startTeach(params: TeachStartParams): Promise<TeachSession> {
    return this.request<TeachSession>(`/navigator/teach/start`, {
      method: "POST",
      body: JSON.stringify(params || {}),
    });
  }

  // "Bana dersi anlat" — hiyerarşik kazanım yürüyüşü (sıra + resume + ilerleme).
  public static async getLessonWalk(lessonId: string): Promise<LessonWalkResponse> {
    return this.request<LessonWalkResponse>(`/navigator/lesson-walk?lessonId=${encodeURIComponent(lessonId)}`);
  }

  // Öğrenme Defteri — kazanımı "anlatıldı" işaretle (video izlendi / AI anlattı).
  public static async recordTaught(gainId: string, opts: { video?: boolean; ai?: boolean }): Promise<any> {
    return this.request<any>(`/navigator/ledger/taught`, {
      method: "POST",
      body: JSON.stringify({ gainId, ...opts }),
    });
  }

  // Öğrenme Defteri — ders için konu başına anlatıldı/test/pekişti özeti.
  public static async getNavigatorLedger(lessonId: string): Promise<LedgerResponse> {
    return this.request<LedgerResponse>(`/navigator/ledger?lessonId=${encodeURIComponent(lessonId)}`);
  }

  // Günlük — "neler yaptın" (anlatım + deneme oturumları, kronolojik).
  public static async getNavigatorJournal(lessonId?: string, limit?: number): Promise<JournalResponse> {
    const qs = new URLSearchParams();
    if (lessonId) qs.set("lessonId", lessonId);
    if (limit) qs.set("limit", String(limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<JournalResponse>(`/navigator/journal${suffix}`);
  }

  // ── Koç: tanışma, plan, bugün ──

  public static async getCoachProfile(): Promise<{ hasProfile: boolean; profile: CoachProfile | null; ready?: boolean }> {
    return this.request(`/navigator/coach/profile`);
  }

  // Tanışma diyaloğu — koç konuşur, niyeti çıkarır, profile işler.
  public static async coachChat(messages: CoachChatMessage[]): Promise<CoachChatResponse> {
    return this.request<CoachChatResponse>(`/navigator/coach/chat`, {
      method: "POST",
      body: JSON.stringify({ messages }),
    });
  }

  // Form-önce tanışma: sihirbaz verisi (dersler, sınav tahmini, varsayılan tarih, kayıttan puan türü).
  public static async getCoachTanisma(): Promise<CoachTanismaForm> {
    return this.request<CoachTanismaForm>(`/navigator/coach/tanisma`);
  }

  // Bölüm otomatik tamamlama — ÖSYM listesinden ad + puan türü + sıralama merdiveni.
  public static async searchCoachBolum(q: string): Promise<{ items: BolumOneri[] }> {
    return this.request<{ items: BolumOneri[] }>(`/navigator/coach/tanisma/bolum?q=${encodeURIComponent(q)}`);
  }

  // YOL teklifi: son TYT denemesi, sabit sıra ve günde 1/2/3/4 saatle dönem sonu beklentisi.
  public static async getCoachYolTeklif(puanTuru?: string | null, sinav?: "YKS" | "LGS" | null): Promise<YolTeklif> {
    const q = new URLSearchParams();
    if (puanTuru) q.set("puanTuru", puanTuru);
    if (sinav) q.set("sinav", sinav);
    const qs = q.toString();
    return this.request<YolTeklif>(`/navigator/coach/yol/teklif${qs ? `?${qs}` : ""}`);
  }

  // Sihirbaz gönderimi: profil + plan (YKS'de haftalık YOL planı) + hoş geldin mektubu (beklenir, ~20 sn).
  public static async submitCoachTanisma(body: CoachTanismaBody): Promise<CoachTanismaResult> {
    return this.request<CoachTanismaResult>(`/navigator/coach/tanisma`, { method: "POST", body: JSON.stringify(body) });
  }

  // Plan-sonrası koç sohbeti — koç plan durumunu bilerek konuşur; gün/dakika ve hedef
  // tarih değişikliklerini profile işler, revisionPending=true ise "Planı güncelle" gösterilir.
  public static async coachPlanChat(messages: CoachChatMessage[]): Promise<CoachPlanChatResponse> {
    return this.request<CoachPlanChatResponse>(`/navigator/coach/plan-chat`, {
      method: "POST",
      body: JSON.stringify({ messages }),
    });
  }

  // Baştan tanış — profili sıfırlar, aktif planı arşivler.
  public static async resetCoachProfile(): Promise<{ reset: boolean; archivedPlanId: string | null }> {
    return this.request(`/navigator/coach/profile/reset`, { method: "POST" });
  }

  // Plan üret (aktif plan varsa revize edilir).
  public static async createCoachPlan(startDate?: string): Promise<any> {
    return this.request(`/navigator/coach/plan`, {
      method: "POST",
      body: JSON.stringify(startDate ? { startDate } : {}),
    });
  }

  public static async getCoachPlan(from?: string, to?: string): Promise<CoachPlanResponse> {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<CoachPlanResponse>(`/navigator/coach/plan${suffix}`);
  }

  // Koçun ana ekranı — bugünün işleri + ilerleme.
  // ── Kitaplarım: ödeme kapısı dışında ──

  public static async getBooks(): Promise<{ books: StudentBookSummary[]; daily: BookDaily[]; quota: BookReadQuota }> {
    return this.request(`/navigator/books`);
  }

  public static async readBookContents(images: File[]): Promise<BookReadResult> {
    const formData = new FormData();
    images.forEach((image) => formData.append("images", image, image.name));
    return this.request<BookReadResult>(`/navigator/books/read`, { method: "POST", body: formData });
  }

  public static async getBook(bookId: string): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}`);
  }

  public static async createBook(body: BookPayload): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books`, { method: "POST", body: JSON.stringify(body) });
  }

  public static async updateBook(bookId: string, body: Partial<BookPayload>): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}`, { method: "PUT", body: JSON.stringify(body) });
  }

  public static async deleteBook(bookId: string): Promise<void> {
    await this.request(`/navigator/books/${encodeURIComponent(bookId)}`, { method: "DELETE" });
  }

  public static async addBookResult(bookId: string, testId: string, body: BookResultBody): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/tests/${encodeURIComponent(testId)}/results`, { method: "POST", body: JSON.stringify(body) });
  }

  public static async updateBookResult(bookId: string, testId: string, resultId: string, body: BookResultBody): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/tests/${encodeURIComponent(testId)}/results/${encodeURIComponent(resultId)}`, { method: "PUT", body: JSON.stringify(body) });
  }

  public static async deleteBookResult(bookId: string, testId: string, resultId: string): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/tests/${encodeURIComponent(testId)}/results/${encodeURIComponent(resultId)}`, { method: "DELETE" });
  }

  public static async planBookTest(bookId: string, testId: string, date: string): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/tests/${encodeURIComponent(testId)}/plan`, { method: "PUT", body: JSON.stringify({ date }) });
  }

  public static async unplanBookTest(bookId: string, testId: string): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/tests/${encodeURIComponent(testId)}/plan`, { method: "DELETE" });
  }

  public static async addBookWrong(bookId: string, testId: string, images: File[], questionNo: number | null, errorType: BookErrorType | null): Promise<StudentBook> {
    const formData = new FormData();
    images.forEach((image) => formData.append("images", image, image.name));
    if (questionNo) formData.append("questionNo", String(questionNo));
    if (errorType) formData.append("errorType", errorType);
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/tests/${encodeURIComponent(testId)}/wrongs`, { method: "POST", body: formData });
  }

  public static async confirmBookWrong(bookId: string, wrongId: string, gainId: string | null): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/wrongs/${encodeURIComponent(wrongId)}/confirm`, { method: "POST", body: JSON.stringify({ gainId }) });
  }

  public static async deleteBookWrong(bookId: string, wrongId: string): Promise<StudentBook> {
    return this.request<StudentBook>(`/navigator/books/${encodeURIComponent(bookId)}/wrongs/${encodeURIComponent(wrongId)}`, { method: "DELETE" });
  }

  public static async searchBookCatalog(q: string): Promise<{ items: BookCatalogItem[] }> {
    return this.request(`/navigator/book-catalog?q=${encodeURIComponent(q)}`);
  }

  public static async getBookCatalog(catalogId: string): Promise<BookPayload> {
    return this.request<BookPayload>(`/navigator/book-catalog/${encodeURIComponent(catalogId)}`);
  }

  public static async getBookReviews(): Promise<BookReviews> {
    return this.request<BookReviews>(`/navigator/book-reviews/today`, { odemeYonlendirmesiz: true });
  }

  public static async answerBookReview(reviewId: string, dogru: boolean): Promise<BookReviews> {
    return this.request<BookReviews>(`/navigator/book-reviews/${encodeURIComponent(reviewId)}`, { method: "POST", body: JSON.stringify({ dogru }), odemeYonlendirmesiz: true });
  }

  // Fotoğraf yalnız sahibine açılır: oturum başlığı gerektiği için img kaynağı olamaz, Blob olarak alınır
  public static async getBookReviewPhoto(reviewId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/navigator/book-reviews/${encodeURIComponent(reviewId)}/photo`, { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error("Fotoğraf açılamadı");
    return response.blob();
  }

  public static async getChildBooks(childId: string): Promise<ObserveBooks> {
    return this.request<ObserveBooks>(`/navigator/children/${encodeURIComponent(childId)}/books`);
  }

  public static async getObserveStudentBooks(userId: string): Promise<ObserveBooks> {
    return this.request<ObserveBooks>(`/navigator/observe/students/${encodeURIComponent(userId)}/books`);
  }

  public static async getCoachToday(date?: string): Promise<CoachTodayResponse> {
    const suffix = date ? `?date=${encodeURIComponent(date)}` : "";
    return this.request<CoachTodayResponse>(`/navigator/coach/today${suffix}`);
  }

  // ── Koç: YKS yol haritası ──

  // Hedef bilgisini profile işle (puan türü, hedef sıralama, son deneme netleri, din muafiyeti).
  // hedefSiralama: null gönderilirse hedef kaldırılır.
  public static async updateCoachProfile(
    body: Partial<{
      puanTuru: "TYT" | "SAY" | "EA" | "SÖZ" | "LGS" | null;
      hedefSiralama: number | null;
      // LGS hedefi yüzdelik dilimle girilir; sunucu katılımcı sayısıyla sıralamaya çevirip saklar.
      hedefYuzdelik: number | null;
      mevcutNetler: Record<string, number | null> | null;
      dinMuaf: boolean;
    }>
  ): Promise<CoachProfile> {
    return this.request<CoachProfile>(`/navigator/coach/profile`, {
      method: "POST",
      body: JSON.stringify(body || {}),
    });
  }

  // Hedefe göre konu önceliği + kapsama (YKS ve LGS). Parametreler profildeki hedefi geçici olarak ezer.
  public static async getCoachRoadmap(params?: { puanTuru?: string; hedefSiralama?: number; hedefYuzdelik?: number }): Promise<YksRoadmapResponse> {
    const qs = new URLSearchParams();
    if (params?.puanTuru) qs.set("puanTuru", params.puanTuru);
    if (params?.hedefSiralama !== undefined && params?.hedefSiralama !== null) {
      qs.set("hedefSiralama", String(params.hedefSiralama));
    }
    if (params?.hedefYuzdelik !== undefined && params?.hedefYuzdelik !== null) {
      qs.set("hedefYuzdelik", String(params.hedefYuzdelik));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<YksRoadmapResponse>(`/navigator/coach/roadmap${suffix}`);
  }

  // Net senaryoları — iki oturumun net kombinasyonlarının yaklaşık yeri
  // (YKS: TYT × AYT → sıralama · LGS: Sayısal × Sözel → yüzdelik dilim).
  public static async getCoachScenario(puanTuru?: string): Promise<YksScenarioResponse> {
    const suffix = puanTuru ? `?puanTuru=${encodeURIComponent(puanTuru)}` : "";
    return this.request<YksScenarioResponse>(`/navigator/coach/scenario${suffix}`);
  }

  // Konu beyanı — öğrenci "bu konuyu biliyorum" der; test verisi yoksa hakimiyet buradan gelir.
  // durum: null gönderilirse işaret kaldırılır.
  public static async setKonuBeyan(
    konuId: string,
    durum: YksBeyanDurumu | null
  ): Promise<{ konuId: string; durum: YksBeyanDurumu | null; selfAssessment: any }> {
    return this.request(`/navigator/coach/roadmap/beyan`, {
      method: "POST",
      body: JSON.stringify({ konuId, durum }),
    });
  }

  public static async updatePlanItemStatus(
    itemId: string,
    status: PlanItemStatus,
    result?: Record<string, any>
  ): Promise<PlanItem> {
    return this.request<PlanItem>(`/navigator/coach/plan/items/${encodeURIComponent(itemId)}`, {
      method: "PATCH",
      body: JSON.stringify(result ? { status, result } : { status }),
    });
  }

  // ── Hata türü ──
  public static async getExamErrorMarks(examId: string): Promise<{ marks: ErrorMark[] }> {
    return this.request(`/navigator/coach/errors?examId=${encodeURIComponent(examId)}`);
  }

  public static async markErrorType(questionId: string, mark: ErrorType): Promise<ErrorMark> {
    return this.request<ErrorMark>(`/navigator/coach/errors/${encodeURIComponent(questionId)}`, {
      method: "POST",
      body: JSON.stringify({ mark }),
    });
  }

  // ── Kişisel telafi seti ──
  // Gözlemci paneli — yönetici / öğretmen / rehberlik (navigator_observe, departman kapsamı)
  public static async observeStudents(params: { q?: string; departmentId?: string; risk?: string; page?: number; limit?: number } = {}): Promise<ObserveStudentsResponse> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<ObserveStudentsResponse>(`/navigator/observe/students${suffix}`);
  }

  public static async observeStudent(userId: string): Promise<ObserveStudentDetail> {
    return this.request<ObserveStudentDetail>(`/navigator/observe/students/${encodeURIComponent(userId)}`);
  }

  public static async observeGenerateLetter(userId: string, kind: string): Promise<{ created: boolean; letter: ObserveLetter }> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/letters/generate`, { method: "POST", body: JSON.stringify({ kind }) });
  }

  // Müdahale: öğretmen programı kurar/değiştirir, öğrenciye not yazar
  public static async observeYol(userId: string): Promise<ObserveYolView> {
    return this.request<ObserveYolView>(`/navigator/observe/students/${encodeURIComponent(userId)}/yol`);
  }

  public static async observeYolApply(userId: string, body: ObserveYolApplyBody): Promise<{ kuruldu: boolean; planId: string | null; view: ObserveYolView }> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/yol`, { method: "POST", body: JSON.stringify(body) });
  }

  // Koçun telefon araması (yapay zekâ asistanı)
  public static async observeTelefon(userId: string): Promise<ObserveCallList> {
    return this.request<ObserveCallList>(`/navigator/observe/students/${encodeURIComponent(userId)}/telefon`);
  }

  public static async observeTelefonAra(userId: string): Promise<{ id: string; status: string }> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/telefon`, { method: "POST" });
  }

  // Öğrenci "beni aramayın" demişti: öğretmen konuşup asistanın aramasını yeniden açar
  public static async observeTelefonAc(userId: string): Promise<{ ok: boolean }> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/telefon/ac`, { method: "POST" });
  }

  public static async observeSetAra(userId: string, body: { bitis: string | null; not: string | null }): Promise<{ ara: unknown }> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/ara`, { method: "POST", body: JSON.stringify(body) });
  }

  public static async observeEndAra(userId: string): Promise<{ ara: unknown }> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/ara`, { method: "DELETE" });
  }

  public static async observePlan(userId: string, range?: { from: string; to: string }): Promise<ObservePlanView> {
    const qs = range ? `?from=${range.from}&to=${range.to}` : "";
    return this.request<ObservePlanView>(`/navigator/observe/students/${encodeURIComponent(userId)}/plan${qs}`);
  }

  public static async observeSendNote(userId: string, text: string): Promise<ObserveLetter> {
    return this.request(`/navigator/observe/students/${encodeURIComponent(userId)}/note`, { method: "POST", body: JSON.stringify({ text }) });
  }

  public static async getPendingCoachLetters(): Promise<{ count: number; items: ObserveLetter[] }> {
    return this.request(`/navigator/coach/letters/pending`);
  }

  public static async approveCoachLetter(id: string, bodyEdited?: string | null): Promise<ObserveLetter> {
    return this.request(`/navigator/coach/letters/${encodeURIComponent(id)}/approve`, { method: "POST", body: JSON.stringify({ bodyEdited: bodyEdited || null }) });
  }

  public static async skipCoachLetter(id: string, reason?: string): Promise<ObserveLetter> {
    return this.request(`/navigator/coach/letters/${encodeURIComponent(id)}/skip`, { method: "POST", body: JSON.stringify({ reason: reason || "" }) });
  }

  // Koçun mektupları
  public static async getCoachLetters(limit?: number): Promise<CoachLettersResponse> {
    return this.request<CoachLettersResponse>(`/navigator/coach/letters${limit ? `?limit=${limit}` : ""}`);
  }

  public static async getCoachLetter(id: string): Promise<CoachLetter> {
    return this.request<CoachLetter>(`/navigator/coach/letters/${encodeURIComponent(id)}`);
  }

  public static async answerCoachLetter(
    id: string,
    body: { text?: string; accept?: boolean; days?: number; minutes?: number }
  ): Promise<{ letter: CoachLetter; ask: { days: number; minutes: number } | null; plan: { moved: number; yolRebuilt: number } | null }> {
    return this.request(`/navigator/coach/letters/${encodeURIComponent(id)}/answer`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // Deneme/manuel: kendim için mektup üret
  public static async generateCoachLetter(kind: string, force = false): Promise<any> {
    return this.request(`/navigator/coach/letters/generate`, { method: "POST", body: JSON.stringify({ kind, force }) });
  }

  public static async getCoachTrajectory(): Promise<any> {
    return this.request(`/navigator/coach/trajectory`);
  }

  public static async getCoachModel(): Promise<any> {
    return this.request(`/navigator/coach/model`);
  }

  // Ölçüm penceresi — hazırbulunuşluk durumu (ders ders + konu konu)
  public static async getCoachReadiness(): Promise<CoachReadinessResponse> {
    return this.request<CoachReadinessResponse>(`/navigator/coach/readiness`);
  }

  // Bir ders için hazırbulunuşluk seti kur (plansız) — runner set.id ile açılır
  public static async buildReadinessSet(lessonId: string): Promise<MakeupSetResponse> {
    return this.request<MakeupSetResponse>(`/navigator/coach/readiness/build`, {
      method: "POST",
      body: JSON.stringify({ lessonId }),
    });
  }

  // Hız ölçümü (son 14 gün) + vites teklifi
  public static async getCoachPace(days?: number): Promise<any> {
    const suffix = days ? `?days=${days}` : "";
    return this.request(`/navigator/coach/pace${suffix}`);
  }

  public static async openMakeupForItem(itemId: string): Promise<MakeupSetResponse> {
    return this.request<MakeupSetResponse>(
      `/navigator/coach/plan/items/${encodeURIComponent(itemId)}/makeup`,
      { method: "POST" }
    );
  }

  public static async getMakeupSet(setId: string): Promise<MakeupSetResponse> {
    return this.request<MakeupSetResponse>(`/navigator/coach/makeup/${encodeURIComponent(setId)}`);
  }

  public static async answerMakeupQuestion(
    setId: string,
    questionId: string,
    answer: any
  ): Promise<{ isCorrect: boolean; solved: number; correct: number; total: number }> {
    return this.request(
      `/navigator/coach/makeup/${encodeURIComponent(setId)}/questions/${encodeURIComponent(questionId)}`,
      { method: "POST", body: JSON.stringify({ answer }) }
    );
  }

  public static async completeMakeupSet(setId: string): Promise<any> {
    return this.request(`/navigator/coach/makeup/${encodeURIComponent(setId)}/complete`, { method: "POST" });
  }

  // AI öğretmen gözlem raporu — kohort özeti (admin/koç, navigator_observe izni).
  public static async getAIObservationOverview(days = 7): Promise<AIObsOverview> {
    return this.request<AIObsOverview>(`/report/ai-observation/overview?days=${days}`);
  }

  // AI öğretmen gözlem — öğrenci detayı (sohbet transkriptleri + aktivite).
  public static async getAIObservationStudentDetail(userId: string): Promise<AIObsStudentDetail> {
    return this.request<AIObsStudentDetail>(`/report/ai-observation/student/${encodeURIComponent(userId)}`);
  }

  public static async getUserGains(filters?: {
    isResolved?: boolean;
    lessonId?: string;
    topicId?: string;
    contentId?: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
    page?: number;
    limit?: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.isResolved !== undefined) params.append("isResolved", String(filters.isResolved));
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);
    if (filters?.contentId) params.append("contentId", filters.contentId);
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    const qs = params.toString();
    return this.request<any>(`/gains${qs ? `?${qs}` : ""}`);
  }

  public static async getGainReports(filters?: {
    lessonId?: string;
    topicId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.lessonId) params.append("lessonId", filters.lessonId);
    if (filters?.topicId) params.append("topicId", filters.topicId);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    const qs = params.toString();
    return this.request<any>(`/gains/reports${qs ? `?${qs}` : ""}`);
  }

  public static async resolveGain(gainId: string): Promise<any> {
    return this.request<any>(`/gains/${gainId}/resolve`, {
      method: "PUT",
    });
  }

  public static async unresolveGain(gainId: string): Promise<any> {
    return this.request<any>(`/gains/${gainId}/unresolve`, {
      method: "PUT",
    });
  }

  public static async getWhiteboards(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.search) qs.append("search", params.search);
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    const query = qs.toString();
    return this.request<any>(`/whiteboards${query ? `?${query}` : ""}`);
  }

  public static async getWhiteboard(whiteboardId: string): Promise<any> {
    return this.request<any>(`/whiteboards/${whiteboardId}`);
  }

  public static async createWhiteboard(data: { name: string; metadata?: any }): Promise<any> {
    return this.postApi("whiteboards", data);
  }

  public static async updateWhiteboard(
    whiteboardId: string,
    data: {
      name?: string;
      liveScreen?: boolean;
      drawings?: any[];
      metadata?: any;
      pdfFileId?: string | null;
      pdfPages?: any[] | null;
      currentPdfPage?: number | null;
    }
  ): Promise<any> {
    return this.putApi(`whiteboards/${whiteboardId}`, data);
  }

  public static async deleteWhiteboard(whiteboardId: string): Promise<void> {
    return this.deleteApi(`whiteboards/${whiteboardId}`);
  }

  public static async shareWhiteboard(
    whiteboardId: string,
    participants: Array<{
      type: "USER" | "DEPARTMENT";
      userId?: string;
      departmentId?: string;
      canDraw?: boolean;
      canErase?: boolean;
    }>
  ): Promise<any> {
    return this.postApi(`whiteboards/${whiteboardId}/share`, { participants });
  }

  public static async stopWhiteboardSharing(whiteboardId: string): Promise<any> {
    return this.postApi(`whiteboards/${whiteboardId}/stop-sharing`, {});
  }

  public static async getWhiteboardParticipants(whiteboardId: string): Promise<any> {
    return this.request<any>(`/whiteboards/${whiteboardId}/participants`);
  }

  public static async updateWhiteboardParticipant(
    whiteboardId: string,
    participantId: string,
    data: { canDraw?: boolean; canErase?: boolean }
  ): Promise<any> {
    return this.putApi(`whiteboards/${whiteboardId}/participants/${participantId}`, data);
  }

  public static async removeWhiteboardParticipant(whiteboardId: string, participantId: string): Promise<void> {
    return this.deleteApi(`whiteboards/${whiteboardId}/participants/${participantId}`);
  }

  public static async updateDepartmentUserPermission(
    whiteboardId: string,
    participantId: string,
    userId: string,
    data: { canDraw?: boolean; canErase?: boolean }
  ): Promise<any> {
    return this.putApi(`whiteboards/${whiteboardId}/participants/${participantId}/users/${userId}`, data);
  }

  public static async joinWhiteboard(whiteboardId: string): Promise<any> {
    return this.postApi(`whiteboards/${whiteboardId}/join`, {});
  }

  public static async excludeDepartmentUser(
    whiteboardId: string,
    participantId: string,
    userId: string
  ): Promise<any> {
    return this.deleteApi(`whiteboards/${whiteboardId}/participants/${participantId}/users/${userId}`);
  }

  public static async getSharedWhiteboards(): Promise<any> {
    return this.request<any>("/whiteboards/shared");
  }

  public static async updateWhiteboardDrawings(
    whiteboardId: string,
    data: { drawings: any[]; action?: "add" | "clear" | "undo" | "set" }
  ): Promise<any> {
    return this.postApi(`whiteboards/${whiteboardId}/drawings`, data);
  }

  public static async uploadWhiteboardPdf(file: File): Promise<{
    file: {
      id: string;
      originalName: string;
      url: string;
    };
    pages: Array<{
      pageNumber: number;
      imageUrl: string;
      width: number;
      height: number;
    }>;
  }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<any>("/whiteboards/upload-pdf", {
      method: "POST",
      body: formData,
    });
  }

  public static async getNotifications(filters?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    filter?: 'all' | 'read' | 'unread';
  }): Promise<{
    results: any[];
    page: number;
    limit: number;
    totalPages: number;
    totalResults: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.filter) params.append("filter", filters.filter);
    const qs = params.toString();
    return this.request<any>(`/notifications${qs ? `?${qs}` : ""}`);
  }

  public static async getUnreadNotificationCount(): Promise<{ unreadCount: number }> {
    return this.request<{ unreadCount: number }>(`/notifications/unread-count`);
  }

  public static async markNotificationAsRead(notificationId: string): Promise<any> {
    return this.request<any>(`/notifications/${notificationId}/read`, {
      method: "POST",
    });
  }

  public static async markAllNotificationsAsRead(): Promise<any> {
    return this.request<any>(`/notifications/mark-all-read`, {
      method: "POST",
    });
  }

  public static async markMultipleNotificationsAsRead(notificationIds: string[]): Promise<any> {
    return this.request<any>(`/notifications/mark-multiple-read`, {
      method: "POST",
      body: JSON.stringify({ notificationIds }),
    });
  }

  public static async getMyErrorBooklets(filters?: {
    page?: number;
    limit?: number;
  }): Promise<{
    data: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();

    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));

    const qs = params.toString();
    return this.request<any>(`/error-booklet/my${qs ? `?${qs}` : ""}`);
  }

  public static async getCurrentMonthErrorBooklet(): Promise<any> {
    return this.request<any>(`/error-booklet/current`);
  }

  public static async getErrorBookletDetails(bookletId: string): Promise<any> {
    return this.request<any>(`/error-booklet/${bookletId}`);
  }

  public static async answerErrorBookletQuestion(
    bookletId: string,
    questionId: string,
    answer: any
  ): Promise<any> {
    return this.request<any>(`/error-booklet/${bookletId}/questions/${questionId}/answer`, {
      method: "POST",
      body: JSON.stringify({ answer }),
    });
  }

  public static async completeErrorBooklet(
    bookletId: string,
    answers: Array<{ questionId: string; answer: any }>
  ): Promise<{
    bookletId: string;
    totalQuestions: number;
    answeredQuestions: number;
    correctAnswers: number;
    incorrectAnswers: number;
    score: number;
    results: Array<{
      questionId: string;
      isCorrect: boolean;
      correctAnswer: any;
      userAnswer: any;
    }>;
  }> {
    return this.request<any>(`/error-booklet/${bookletId}/complete`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    });
  }

  public static async getTeacherHomeworkReport(params?: {
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    limit?: number;
    filter?: {
      teacherId?: string;
      departmentId?: string;
      lessonId?: string;
      startDate?: string;
      endDate?: string;
      completionStatus?: string;
      weekNumbers?: number[];
    };
  }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.sortBy) qs.append("sortBy", params.sortBy);
    if (params?.sortOrder) qs.append("sortOrder", params.sortOrder);
    if (params?.page) qs.append("page", String(params.page));
    if (params?.limit) qs.append("limit", String(params.limit));
    if (params?.filter) {
      Object.entries(params.filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v, i) => qs.append(`filter[${key}][${i}]`, String(v)));
          } else {
            qs.append(`filter[${key}]`, String(value));
          }
        }
      });
    };

    const query = qs.toString();
    return this.request<any>(`/report/teacher/homework-report${query ? `?${query}` : ""}`);
  }

  public static async getAvailableTeachersForReport(): Promise<any[]> {
    return this.request<any[]>(`/report/teacher/homework-report/teachers`);
  }

  public static async getAvailableLessonsForTeacherReport(): Promise<any[]> {
    return this.request<any[]>(`/report/teacher/homework-report/lessons`);
  }

  public static async exportTeacherHomeworkReport(filter?: {
    teacherId?: string;
    departmentId?: string;
    lessonId?: string;
    startDate?: string;
    endDate?: string;
    completionStatus?: string;
    weekNumbers?: number[];
  }): Promise<Blob> {
    const qs = new URLSearchParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v, i) => qs.append(`filter[${key}][${i}]`, String(v)));
          } else {
            qs.append(`filter[${key}]`, String(value));
          }
        }
      });
    };

    const query = qs.toString();
    const endpoint = `/report/teacher/homework-report/export${query ? `?${query}` : ""}`;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = `HTTP Hatası! Durum: ${response.status}`;
      throw new Error(errorMessage);
    };

    return response.blob();
  }

  public static async getTeacherDepartments(teacherId: string): Promise<any[]> {
    return this.request<any[]>(`/report/teacher/homework-report/${teacherId}/departments`);
  }

  public static async getTeacherWeeks(teacherId: string, departmentId?: string): Promise<any[]> {
    const qs = new URLSearchParams();
    if (departmentId) qs.append("departmentId", departmentId);
    const query = qs.toString();
    return this.request<any[]>(`/report/teacher/homework-report/${teacherId}/weeks${query ? `?${query}` : ""}`);
  }

  public static async getWeakTopicsForHomeworkReport(reportId: string): Promise<{
    weakTopics: Array<{
      gainId: string;
      gainText: string;
      startTime?: string;
      endTime?: string;
      questionCount: number;
      questionOrders: number[];
      topic?: { id: string; title: string } | null;
      lesson?: { id: string; title: string } | null;
      content?: { id: string; title: string; type: string } | null;
      video?: { id: string; title: string; type: string; url: string | null } | null;
    }>;
    wrongTopics: Array<any>;
    unansweredTopics: Array<any>;
  }> {
    return this.request<any>(`/report/homework-report/${reportId}/weak-topics`);
  }

  public static async getEducationSettings(): Promise<{
    teacherDepartmentId: string | null;
    parentDepartmentId: string | null;
    studentDepartmentId: string | null;
    teacherDepartment: { id: string; name: string; color?: string } | null;
    parentDepartment: { id: string; name: string; color?: string } | null;
    studentDepartment: { id: string; name: string; color?: string } | null;
  }> {
    return this.request<any>(`/settings/education`);
  }

  public static async updateEducationSettings(data: {
    teacherDepartmentId?: string | null;
    parentDepartmentId?: string | null;
    studentDepartmentId?: string | null;
  }): Promise<any> {
    return this.postApi("settings/education", data);
  }

  public static async getCafeSettings(): Promise<{
    cafeUnregisteredStudentDepartmentId: string | null;
    cafeGuestUserDepartmentId: string | null;
    cafeRegistrationApiKey: string | null;
    cafeMenuApiKey: string | null;
    cafeMuhasebeBranchId: string | null;
    cafeMuhasebeBranchName: string | null;
    cafeUnregisteredStudentDepartment: { id: string; name: string; color?: string } | null;
    cafeGuestUserDepartment: { id: string; name: string; color?: string } | null;
  }> {
    return this.request<any>(`/settings/cafe`);
  }

  public static async getCafePublicSettings(): Promise<{
    cafeUnregisteredStudentDepartmentId: string | null;
    cafeGuestUserDepartmentId: string | null;
    cafeMuhasebeBranchId: string | null;
    cafeMuhasebeBranchName: string | null;
  }> {
    return this.request<any>(`/settings/cafe/public`);
  }

  public static async updateCafeSettings(data: {
    cafeUnregisteredStudentDepartmentId?: string | null;
    cafeGuestUserDepartmentId?: string | null;
    cafeRegistrationApiKey?: string | null;
    cafeMenuApiKey?: string | null;
    cafeMuhasebeBranchId?: string | null;
    cafeMuhasebeBranchName?: string | null;
  }): Promise<any> {
    return this.postApi("settings/cafe", data);
  }

  public static async getCafeMuhasebeHealth(): Promise<{
    success?: boolean;
    status?: string;
    timestamp?: string;
  }> {
    return this.request<any>(`/settings/cafe/muhasebe/health`);
  }

  public static async getCafeMuhasebeBranches(): Promise<any> {
    return this.request<any>(`/settings/cafe/muhasebe/branches`);
  }

  public static async getCafeMenuCatalog(params?: {
    categoryId?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const query = new URLSearchParams();

    if (params?.categoryId) query.set("categoryId", params.categoryId);
    if (params?.search) query.set("search", params.search);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined) query.set("offset", String(params.offset));

    return this.request<any>(`/cafe-menu/catalog${query.toString() ? `?${query.toString()}` : ""}`);
  }

  public static async getCafeCampaigns(): Promise<any> {
    return this.request<any>(`/cafe-menu/campaigns`);
  }

  public static async getN8nSettings(): Promise<{
    n8nLatexExtractorWebhookUrl: string | null;
  }> {
    return this.request<any>(`/settings/n8n`);
  }

  public static async updateN8nSettings(data: {
    n8nLatexExtractorWebhookUrl?: string | null;
  }): Promise<any> {
    return this.postApi("settings/n8n", data);
  }

  public static async getAITeacherSettings(): Promise<{
    aiTeacherAudioModel: string;
    selectedAudioModel?: { id: string; label: string; mode: "tts" | "live" };
    supportedAudioModels?: Array<{ id: string; label: string; mode: "tts" | "live" }>;
  }> {
    return this.request<any>(`/settings/ai-teacher`);
  }

  // Koç mektubu ayarları: imza adı, kurumun referans çizgisi, otomatik onay
  public static async getCoachSettings(): Promise<{ coachName: string; coachReferans: Record<string, number>; coachLetterAutoApprove: boolean; defaults: any }> {
    return this.getApi("settings/coach");
  }

  public static async updateCoachSettings(data: { coachName?: string | null; coachReferans?: Record<string, number>; coachLetterAutoApprove?: boolean }): Promise<any> {
    return this.postApi("settings/coach", data);
  }

  // Koçun telefon araması (Sistem Ayarları → Telefon Asistanı): gizli alanlar maskeli döner
  public static async getCoachCallSettings(): Promise<CoachCallSettings> {
    return this.getApi("settings/coach-call");
  }

  public static async updateCoachCallSettings(data: Partial<CoachCallSettingsBody>): Promise<CoachCallSettings> {
    return this.postApi("settings/coach-call", data);
  }

  public static async testCoachCall(phone: string, tur?: "SESSIZLIK" | "ILK_ZIYARET" | "BAG"): Promise<{ id: string; status: string }> {
    return this.postApi("settings/coach-call/test", { phone, ...(tur ? { tur } : {}) });
  }

  // Son görüşmeler: bütün arama türleri (akademi aramalarındaki yeni kayıtlılar Koç Gözlem'de görünmez)
  public static async getCoachCalls(params: { reason?: string; limit?: number } = {}): Promise<import("../types/navigator").ObserveCallRow[]> {
    const q = new URLSearchParams();
    if (params.reason) q.set("reason", params.reason);
    if (params.limit) q.set("limit", String(params.limit));
    return this.getApi(`settings/coach-call/calls${q.toString() ? `?${q}` : ""}`);
  }

  public static async updateAITeacherSettings(data: {
    aiTeacherAudioModel: string;
  }): Promise<any> {
    return this.postApi("settings/ai-teacher", data);
  }

  public static async getErrorBookletAvailableMonths(): Promise<Array<{ year: number; month: number }>> {
    return this.request<any>(`/error-booklet/reports/available-months`);
  }

  public static async getErrorBookletDepartmentReport(
    departmentId: string,
    year: number,
    month: number,
    options?: {
      page?: number;
      limit?: number;
      statuses?: string[];
    }
  ): Promise<{
    data: Array<{
      userId: string;
      firstName: string;
      lastName: string;
      schoolNumber?: string;
      department?: { id: string; name: string; color: string };
      hasBooklet: boolean;
      bookletId: string | null;
      totalQuestions: number;
      solvedQuestions: number;
      correctAnswers: number;
      wrongAnswers: number;
      emptyAnswers: number;
      status: string;
      completedAt: string | null;
      successRate: number;
    }>;
    meta: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
      perPage: number;
    };
    summary: {
      year: number;
      month: number;
      totalStudents: number;
      studentsWithBooklets: number;
      noBookletStudents: number;
      completedBooklets: number;
      inProgressBooklets: number;
      pendingBooklets: number;
      totalQuestions: number;
      totalSolved: number;
      totalCorrect: number;
      totalWrong: number;
      averageSuccessRate: number;
    };
    availableStatuses: string[];
  }> {
    const params = new URLSearchParams();
    params.append("year", String(year));
    params.append("month", String(month));
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.statuses && options.statuses.length > 0) {
      options.statuses.forEach(status => params.append("statuses", status));
    }
    return this.request<any>(`/error-booklet/reports/department/${departmentId}?${params.toString()}`);
  }

  public static async getErrorBookletStudentReport(
    userId: string,
    year: number,
    month: number
  ): Promise<{
    user: {
      id: string;
      firstName: string;
      lastName: string;
      schoolNumber?: string;
      department?: { id: string; name: string; color: string };
    };
    year: number;
    month: number;
    hasBooklet: boolean;
    booklet: {
      id: string;
      status: string;
      totalQuestions: number;
      solvedQuestions: number;
      correctAnswers: number;
      wrongAnswers: number;
      emptyAnswers: number;
      successRate: number;
      startedAt: string | null;
      completedAt: string | null;
    } | null;
    questions: Array<{
      id: string;
      order: number;
      lessonName: string;
      lessonId: string;
      sectionName: string;
      gain: string | null;
      answer: any;
      isCorrect: boolean | null;
      previousWrongAnswer: any;
      correctOption: any;
      questionContent: string | null;
      questionMedia: { id: string; url: string; type: string } | null;
    }>;
    lessonSummary: Array<{
      lessonId: string;
      lessonName: string;
      totalQuestions: number;
      correctAnswers: number;
      wrongAnswers: number;
      emptyAnswers: number;
      successRate: number;
    }>;
  }> {
    return this.request<any>(`/error-booklet/reports/student/${userId}?year=${year}&month=${month}`);
  }

  public static async resetStudentErrorBooklet(
    userId: string,
    year: number,
    month: number
  ): Promise<{
    success: boolean;
    message: string;
    booklet: {
      id: string;
      userId: string;
      year: number;
      month: number;
      status: string;
    };
  }> {
    return this.postApi(`error-booklet/reports/student/${userId}/reset`, { year, month });
  }

  public static async getUserPracticeExams(filters?: {
    page?: number;
    limit?: number;
    sortBy?: string;
  }): Promise<{
    count: number;
    rows: PracticeExam[];
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);

    const qs = params.toString();
    return this.request<any>(`/practice-exams/my-exams${qs ? `?${qs}` : ""}`);
  }

  static async getSmsSettings(): Promise<{
    isCustomSms: boolean;
    smsProvider: string | null;
    smsApiKey: string | null;
    smsApiSecret: string | null;
    smsSenderName: string | null;
  }> {
    return this.getApi('settings/sms');
  }

  static async updateSmsSettings(data: {
    isCustomSms: boolean;
    smsProvider?: string | null;
    smsApiKey?: string | null;
    smsApiSecret?: string | null;
    smsSenderName?: string | null;
  }): Promise<any> {
    return this.postApi('settings/sms', data);
  }

  static async getRegistrationSettings(): Promise<{
    isRegistrationEnabled: boolean;
    registrationPromptText?: string | null;
    isPasswordResetEnabled?: boolean;
    passwordResetPromptText?: string | null;
    pendingApprovalDepartmentId?: string | null;
    pendingApprovalDepartment?: { id: string; name: string; color?: string } | null;
    registrationDepartmentIds?: string[];
    registrationDepartments?: { id: string; name: string }[];
  }> {
    return this.getApi('settings/registration');
  }

  // Kayıtta görünecek departmanları güncelle (admin).
  static async updateRegistrationDepartments(registrationDepartmentIds: string[]): Promise<{ registrationDepartmentIds: string[] }> {
    return this.postApi('settings/registration-departments', { registrationDepartmentIds });
  }

  static async updateRegistrationSettings(data: {
    isRegistrationEnabled: boolean;
    registrationPromptText?: string | null;
    isPasswordResetEnabled?: boolean;
    passwordResetPromptText?: string | null;
    pendingApprovalDepartmentId?: string | null;
  }): Promise<{
    isRegistrationEnabled: boolean;
    registrationPromptText?: string | null;
    isPasswordResetEnabled?: boolean;
    passwordResetPromptText?: string | null;
    pendingApprovalDepartmentId?: string | null;
    pendingApprovalDepartment?: { id: string; name: string; color?: string } | null;
  }> {
    return this.postApi('settings/registration', data);
  }

  static async registerUser(data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    rePassword: string;
    birthDate?: string;
    parentEmail?: string;
    phone?: string;
    selectedClassLevel?: string;
    selectedGraduateTrack?: string;
    selectedDepartmentId?: string;
    referralCode?: string;
    kvkkOnay: true;
    pazarlamaOnay?: boolean;
    turnstileToken: string;
  }): Promise<{ message: string; requiresPhoneVerification?: boolean; userId?: string; phone?: string }> {
    return this.postApi('auth/register', data);
  }

  // KVKK aydınlatma metni (public): kayıt formunda zorunlu onaylanan sürümlü metin
  static async getAydinlatmaMetni(): Promise<{ surum: string; kurum: string; bolumler: { baslik: string; metin: string }[] }> {
    return this.request<{ surum: string; kurum: string; bolumler: { baslik: string; metin: string }[] }>(`/auth/aydinlatma-metni`);
  }

  // Kayıtta görünecek departmanlar (public — sistem ayarında tanımlıysa).
  static async getRegistrationDepartments(): Promise<{ departments: { id: string; name: string }[]; whatsapp?: { acik: boolean; numara?: string | null } }> {
    return this.request<{ departments: { id: string; name: string }[]; whatsapp?: { acik: boolean; numara?: string | null } }>(`/auth/registration-departments`);
  }

  // QR ekranındaki hoş geldin kartı: ad, bugünün sıradaki işi, dün bitirilenler
  static async getHosgeldin(): Promise<{ ad: string | null; sonraki: { baslik: string; dakika: number | null } | null; bugunIs: number; dunBiten: number }> {
    return this.request(`/navigator/coach/hosgeldin`);
  }

  // Koçum kartı: ödev ve hatırlatmalar WhatsApp'tan gelsin mi (öğrencinin kararı)
  static async getWhatsappIzin(): Promise<{ goster: boolean; izin: boolean | null; numara: string | null }> {
    return this.request(`/navigator/coach/whatsapp-izin`);
  }

  static async setWhatsappIzin(izin: boolean): Promise<{ goster: boolean; izin: boolean | null; numara: string | null }> {
    return this.request(`/navigator/coach/whatsapp-izin`, { method: "POST", body: JSON.stringify({ izin }) });
  }

  // ===== Şifremi unuttum (public — token gerekmez) =====

  // E-postaya 6 haneli kod gönderir. Sunucu, adres kayıtlı olsun olmasın aynı mesajı döner
  // (kullanıcı sayımını engellemek için).
  static async forgetPassword(email: string): Promise<{ message: string }> {
    return this.postApi('auth/forget-password', { email });
  }

  // E-posta + kod + yeni şifre, tek adımda.
  static async resetPassword(data: { email: string; code: string; password: string; rePassword: string }): Promise<{ message: string }> {
    return this.postApi('auth/reset-password', data);
  }

  // ===== YKS Tercih Robotu (public — token gerekmez) =====
  static async getTercihFiltreler(puanTuru?: string): Promise<import("../types/tercih").FiltreSecenekleri> {
    const qs = puanTuru ? `?puanTuru=${encodeURIComponent(puanTuru)}` : "";
    return this.request(`/tercih-robotu/filtreler${qs}`);
  }

  static async tercihAra(body: {
    puanTuru: string;
    basariSirasi: number;
    filtreler?: import("../types/tercih").TercihFiltreler;
  }): Promise<import("../types/tercih").AraSonuc> {
    return this.request(`/tercih-robotu/ara`, { method: "POST", body: JSON.stringify(body) });
  }

  static async tercihAiYorum(body: {
    puanTuru: string;
    basariSirasi: number;
    filtreler?: import("../types/tercih").TercihFiltreler;
  }): Promise<import("../types/tercih").AiYorumSonuc> {
    return this.request(`/tercih-robotu/ai-yorum`, { method: "POST", body: JSON.stringify(body) });
  }

  static async tercihLeadKaydet(
    body: import("../types/tercih").TercihLeadInput
  ): Promise<{ id: string; mesaj: string }> {
    return this.request(`/tercih-robotu/lead`, { method: "POST", body: JSON.stringify(body) });
  }

  static async tercihAiListe(body: {
    puanTuru: string;
    basariSirasi: number;
    filtreler?: import("../types/tercih").TercihFiltreler;
    adSoyad?: string;
    email?: string;
    telefon?: string;
    kvkkOnay?: boolean;
  }): Promise<import("../types/tercih").AiListeSonuc> {
    return this.request(`/tercih-robotu/ai-liste`, { method: "POST", body: JSON.stringify(body) });
  }

  // Admin (tercih_lead_view izni)
  static async getTercihLeads(params: { page?: number; limit?: number; q?: string } = {}): Promise<
    import("../types/tercih").TercihLeadListe
  > {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.q) qs.set("q", params.q);
    const s = qs.toString();
    return this.request(`/tercih-robotu/admin/leads${s ? `?${s}` : ""}`);
  }

  static async exportTercihLeadsCsv(): Promise<{ blob: Blob; fileName?: string }> {
    return this.requestBlob(`/tercih-robotu/admin/leads/export`);
  }

  static async updateMyClassSelection(data: {
    selectedClassLevel: string;
    selectedGraduateTrack?: string;
  }): Promise<{
    message: string;
    selectedClassLevel: string;
    selectedClassLabel: string;
    selectedGraduateTrack?: string | null;
    selectedGraduateTrackLabel?: string | null;
    needsClassSelection: false;
    department: { id: string; name: string; color?: string; parentId?: string | null };
    metadata: any;
  }> {
    return this.putApi("users/me/class-selection", data);
  }

  static async verifyPhone(userId: string, code: string): Promise<{
    message: string;
    autoLogin?: boolean;
    token?: string;
    refreshToken?: string;
    user?: any;
  }> {
    return this.postApi('auth/verify-phone', { userId, code });
  }

  static async resendPhoneCode(userId: string): Promise<{ message: string; phone?: string }> {
    return this.postApi('auth/resend-phone-code', { userId });
  }

  static async getExamDates(): Promise<{ examDates: Array<{ id: string; name: string; date: string; departmentIds?: string[] }> }> {
    return this.getApi('settings/exam-dates');
  }

  static async updateExamDates(examDates: Array<{ id: string; name: string; date: string; departmentIds?: string[] }>): Promise<{ examDates: Array<{ id: string; name: string; date: string; departmentIds?: string[] }> }> {
    return this.postApi('settings/exam-dates', { examDates });
  }

  static async startFocusSession(data: {
    type: "timer" | "stopwatch" | "pomodoro";
    source?: string | null;
    goalDurationSeconds?: number | null;
    elapsedSeconds?: number;
    remainingSeconds?: number | null;
    moduleKey?: string;
    metadata?: any;
  }): Promise<any> {
    return this.postApi("time-tracking/focus-sessions", data);
  }

  static async recordFocusEvent(sessionId: string, data: {
    eventType: "started" | "paused" | "resumed" | "cancelled" | "completed" | "ended" | "phase_started" | "phase_completed" | "phase_skipped";
    occurredAt?: string;
    elapsedSeconds?: number;
    remainingSeconds?: number | null;
    status?: "running" | "paused" | "cancelled" | "completed" | "ended";
    metadata?: any;
  }): Promise<any> {
    return this.postApi(`time-tracking/focus-sessions/${sessionId}/events`, data);
  }

  static async getMyFocusSessions(filters?: {
    page?: number;
    limit?: number;
    type?: "timer" | "stopwatch" | "pomodoro";
    status?: "running" | "paused" | "cancelled" | "completed" | "ended" | "active";
    startDate?: string;
    endDate?: string;
  }): Promise<BaseResponse & { data: any[] }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    if (filters?.type) params.append("type", filters.type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    const qs = params.toString();
    return this.request<any>(`/time-tracking/focus-sessions${qs ? `?${qs}` : ""}`);
  }

  static async getMyFocusStats(filters?: {
    startDate?: string;
    endDate?: string;
    moduleKey?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.moduleKey) params.append("moduleKey", filters.moduleKey);
    const qs = params.toString();
    return this.request<any>(`/time-tracking/focus-stats${qs ? `?${qs}` : ""}`);
  }

  static async heartbeatPresence(data: {
    sessionId?: string | null;
    moduleKey: string;
    pathname: string;
    isVisible?: boolean;
    isInteractive?: boolean;
    metadata?: any;
  }): Promise<any> {
    return this.postApi("time-tracking/presence/heartbeat", data);
  }

  static async closePresenceSession(data: {
    sessionId?: string | null;
    moduleKey?: string | null;
    pathname?: string | null;
    reason: "idle_closed" | "hidden_closed" | "route_changed" | "logout_closed" | "stale_closed";
    metadata?: any;
  }): Promise<any> {
    return this.postApi("time-tracking/presence/close", data);
  }

  static async getMyActivityStats(filters?: {
    startDate?: string;
    endDate?: string;
    moduleKey?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.moduleKey) params.append("moduleKey", filters.moduleKey);
    const qs = params.toString();
    return this.request<any>(`/time-tracking/activity-stats${qs ? `?${qs}` : ""}`);
  }

  static async getLeaderboard(filters?: {
    startDate?: string;
    endDate?: string;
    moduleKey?: string;
    metric?: "activeSeconds" | "focusSeconds" | "completedFocusCount" | "completedPomodoroCount";
    limit?: number;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.moduleKey) params.append("moduleKey", filters.moduleKey);
    if (filters?.metric) params.append("metric", filters.metric);
    if (filters?.limit) params.append("limit", String(filters.limit));
    const qs = params.toString();
    return this.request<any>(`/time-tracking/leaderboard${qs ? `?${qs}` : ""}`);
  }

  static async getAdminUsersActivityList(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    departmentId?: string | null;
  }): Promise<BaseResponse & { data: any[] }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    if (filters?.search) params.append("search", filters.search);
    if (filters?.departmentId) params.append("departmentId", filters.departmentId);
    const qs = params.toString();
    return this.request<any>(`/time-tracking/admin/users${qs ? `?${qs}` : ""}`);
  }

  static async getAdminUserActivityOverview(userId: string, filters?: {
    startDate?: string;
    endDate?: string;
    moduleKey?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.moduleKey) params.append("moduleKey", filters.moduleKey);
    const qs = params.toString();
    return this.request<any>(`/time-tracking/admin/users/${userId}/overview${qs ? `?${qs}` : ""}`);
  }

  static async getSocialProviders(): Promise<any[]> {
    return this.getApi("social/providers");
  }

  static async upsertSocialProvider(data: {
    provider: string;
    apiKey: string;
    defaultTextModel?: string | null;
    defaultImageModel?: string | null;
    defaultVideoModel?: string | null;
    isActive?: boolean;
  }): Promise<any> {
    return this.putApi("social/providers", data);
  }

  static async testSocialProvider(provider: string, data?: { apiKey?: string }): Promise<any> {
    return this.postApi(`social/providers/${provider}/test`, data || {});
  }

  static async uploadSocialMedia(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    return this.request<any>("/social/media", {
      method: "POST",
      body: formData,
    });
  }

  static async getSocialBrands(): Promise<any[]> {
    return this.getApi("social/brands");
  }

  static async analyzeSocialBrandColors(data: {
    mediaId?: string | null;
    url?: string | null;
  }): Promise<{
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      text: string;
    };
    palette: Array<{ hex: string; count: number }>;
  }> {
    return this.postApi("social/brands/logo-colors", data);
  }

  static async createSocialBrand(data: any): Promise<any> {
    return this.postApi("social/brands", data);
  }

  static async updateSocialBrand(brandId: string, data: any): Promise<any> {
    return this.putApi(`social/brands/${brandId}`, data);
  }

  static async deleteSocialBrand(brandId: string): Promise<any> {
    return this.deleteApi(`social/brands/${brandId}`);
  }

  static async generateSocialText(data: any): Promise<any> {
    return this.postApi("social/generate/text", data);
  }

  static async generateSocialImage(data: any): Promise<any> {
    return this.postApi("social/generate/image", data);
  }

  static async generateSocialVideo(data: any): Promise<any> {
    return this.postApi("social/generate/video", data);
  }

  static async getSocialVideoStatus(jobId: string): Promise<any> {
    return this.getApi(`social/generate/video/${jobId}`);
  }

  static async getSocialGeneratedContents(params?: {
    type?: string;
    status?: string;
    limit?: number;
  }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set("type", params.type);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.limit) searchParams.set("limit", String(params.limit));
    const query = searchParams.toString();
    return this.getApi(`social/generated${query ? `?${query}` : ""}`);
  }

  static async deleteSocialGeneratedContent(id: string): Promise<any> {
    return this.deleteApi(`social/generated/${id}`);
  }

  static async updateDashboardWidgets(widgets: string[]): Promise<{ widgets: string[] }> {
    return this.postApi('settings/dashboard-widgets', { widgets });
  }

  // ── Mail Pazarlama ──────────────────────────────────────────────────────────
  static async getMailSmtpStatus(): Promise<any> { return this.getApi('mail-marketing/smtp/status'); }
  static async sendMailSmtpTest(to: string): Promise<any> { return this.postApi('mail-marketing/smtp/test', { to }); }
  static async previewMailAudience(audience: any): Promise<any> { return this.postApi('mail-marketing/audience/preview', { audience }); }
  static async getMailCampaigns(page = 1, limit = 20): Promise<any> { return this.getApi(`mail-marketing/campaigns?page=${page}&limit=${limit}`); }
  static async getMailCampaign(id: string): Promise<any> { return this.getApi(`mail-marketing/campaigns/${id}`); }
  static async createMailCampaign(data: { name: string; type: 'SERVICE' | 'COMMERCIAL'; mode?: 'BEHAVIOR' | 'TEMPLATE'; channel: 'EMAIL' | 'SMS' | 'BOTH'; audience: any; brief?: string; template?: string; subject?: string }): Promise<any> { return this.postApi('mail-marketing/campaigns', data); }
  static async deleteMailCampaign(id: string): Promise<any> { return this.deleteApi(`mail-marketing/campaigns/${id}`); }
  static async generateMailMessages(id: string, opts: { sample?: number; all?: boolean }): Promise<any> { return this.postApi(`mail-marketing/campaigns/${id}/generate`, opts); }
  static async getMailMessages(id: string, status = '', page = 1, limit = 50): Promise<any> { return this.getApi(`mail-marketing/campaigns/${id}/messages?status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`); }
  static async approveMailCampaign(id: string): Promise<any> { return this.postApi(`mail-marketing/campaigns/${id}/approve`, {}); }
  static async cancelMailCampaign(id: string): Promise<any> { return this.postApi(`mail-marketing/campaigns/${id}/cancel`, {}); }
  static async setMailConsent(data: { userId: string; channel?: 'EMAIL' | 'SMS'; status: 'GRANTED' | 'REVOKED' | 'SUPPRESSED'; reason?: string }): Promise<any> { return this.postApi('mail-marketing/consents', data); }
  static async getSmtpSettings(): Promise<any> { return this.getApi('settings/smtp'); }
  static async updateSmtpSettings(data: { isCustomSmtp: boolean; smtpHost?: string | null; smtpPort?: number | null; smtpUserName?: string | null; smtpPassword?: string | null; smtpSecure?: boolean }): Promise<any> { return this.postApi('settings/smtp', data); }
}

export type { AuthSystemConfig, LoginCredentials, AuthResponse } from "../types/auth";
export type { SurveyResponse, SurveyFilters } from "../types/survey";
export type {
  MeetingResponse,
  MeetingDetailResponse,
  MeetingParticipantsResponse,
  CreateMeetingData,
  UpdateMeetingData,
  CreateParticipantData,
  MeetingFilters,
} from "../types/meeting";
export type {
  StudentReportData,
  LessonStatistic,
  TopicStatistic,
  StudentTest,
  LessonOption,
  TopicOption,
  StudentReportType,
  StudentReportFilters,
} from "../types/student-report";
